import React, { useState, useEffect, useContext, createContext, useRef, useCallback } from 'react';
import './App.css';
import './mobile-optimizations.css';
import { languages, translations, getTranslation, defaultAvatars, getAvatarUrl, svgToBase64 } from './i18n';

// Import new X platform features
import { PostInteractions, FollowButton, TrendingHashtags, UserSuggestions, CommentsModal, QuoteModal, QuotedPostEmbed } from './components/XPlatformFeatures';
import { NotificationsPanel, NotificationBadge, NOTIFICATION_TYPES } from './components/NotificationSystem';
import UserProfile from './components/UserProfile';
import SearchPage from './components/SearchPage';
import DirectMessages from './components/DirectMessages';
import ModerationPanel, { ReportModal, BlockUserModal } from './components/ModerationPanel';
import RoomChat from './components/RoomChat';
import WorldChatTypingIndicator from './components/WorldChatTypingIndicator';

// Messenger-style Input Hook pentru funcționalități multimedia
const useMessengerInput = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Camera/Photo capture
  const capturePhoto = async () => {
    try {
      // Check if camera is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera nu este disponibilă în acest browser');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }, 
        audio: false 
      });
      
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();
      
      return new Promise((resolve, reject) => {
        video.addEventListener('loadedmetadata', () => {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0);
          
          // Stop camera
          stream.getTracks().forEach(track => track.stop());
          
          canvas.toBlob((blob) => {
            if (blob) {
              const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
              resolve(file);
            } else {
              reject(new Error('Nu s-a putut procesa imaginea'));
            }
          }, 'image/jpeg', 0.8);
        });

        video.addEventListener('error', (err) => {
          stream.getTracks().forEach(track => track.stop());
          reject(new Error('Eroare la accesarea camerei'));
        });

        // Timeout după 10 secunde
        setTimeout(() => {
          stream.getTracks().forEach(track => track.stop());
          reject(new Error('Timeout la accesarea camerei'));
        }, 10000);
      });
    } catch (error) {
      console.error('Error accessing camera:', error);
      
      // Fallback - show user-friendly message based on error type
      if (error.name === 'NotFoundError') {
        throw new Error('Nu s-a găsit nicio cameră disponibilă');
      } else if (error.name === 'NotAllowedError') {
        throw new Error('Accesul la cameră a fost refuzat. Verifică permisiunile browserului.');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Camera nu este suportată în acest browser');
      } else {
        throw new Error(error.message || 'Nu se poate accesa camera');
      }
    }
  };

  // Gallery/File selection
  const selectFromGallery = () => {
    return new Promise((resolve, reject) => {
      try {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*,video/*';
        input.multiple = true;
        input.style.display = 'none';
        
        let isResolved = false;
        
        input.onchange = (e) => {
          if (!isResolved) {
            isResolved = true;
            const files = Array.from(e.target.files || []);
            console.log('Files selected:', files.length);
            
            // Validate files
            const validFiles = files.filter(file => {
              const isImage = file.type.startsWith('image/');
              const isVideo = file.type.startsWith('video/');
              const sizeOk = file.size <= 10 * 1024 * 1024; // 10MB limit
              
              if (!isImage && !isVideo) {
                alert(`Fișierul "${file.name}" nu este o imagine sau video valid.`);
                return false;
              }
              
              if (!sizeOk) {
                alert(`Fișierul "${file.name}" este prea mare. Limita este 10MB.`);
                return false;
              }
              
              return true;
            });
            
            resolve(validFiles);
            document.body.removeChild(input);
          }
        };
        
        // Handle cancel (focus return without selection)
        input.oncancel = () => {
          if (!isResolved) {
            isResolved = true;
            console.log('File selection cancelled by user');
            resolve([]);
            document.body.removeChild(input);
          }
        };

        // Add to DOM and trigger click
        document.body.appendChild(input);
        input.click();
        
        // Fallback timeout for browsers that don't support oncancel
        setTimeout(() => {
          if (!isResolved) {
            isResolved = true;
            console.log('File selection timeout - likely cancelled');
            resolve([]);
            if (document.body.contains(input)) {
              document.body.removeChild(input);
            }
          }
        }, 30000); // 30 second timeout

      } catch (error) {
        console.error('Error in selectFromGallery:', error);
        reject(new Error('Nu s-a putut deschide selectorul de fișiere'));
      }
    });
  };

  // Voice recording
  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);
      
      const recorder = new MediaRecorder(stream);
      setMediaRecorder(recorder);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `voice_${Date.now()}.webm`, { type: 'audio/webm' });
        
        // Cleanup
        stream.getTracks().forEach(track => track.stop());
        setMediaStream(null);
        setMediaRecorder(null);
        
        // Return voice file
        window.lastVoiceFile = file;
      };
      
      recorder.start();
      setIsRecording(true);
      
      return recorder;
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Nu se poate accesa microfonul. Verifică permisiunile.');
      return null;
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return {
    capturePhoto,
    selectFromGallery,
    startVoiceRecording,
    stopVoiceRecording,
    isRecording
  };
};

// Avatar Dropdown Component - Emergent/X Style
const AvatarDropdown = ({ user, onAccountSettings, onLogout, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="absolute top-full right-0 mt-2 w-64 bg-gray-800/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-xl overflow-hidden z-50">
      {/* User Profile Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-500">
              {user?.avatar_url ? (
                user.avatar_url.startsWith('data:') ? (
                  <img 
                    src={user.avatar_url} 
                    alt="Avatar" 
                    className="w-full h-full object-cover retina-optimized" 
                  />
                ) : (
                  <div 
                    className="w-full h-full"
                    dangerouslySetInnerHTML={{ 
                      __html: defaultAvatars.find(a => a.id === user.avatar_url)?.svg || '' 
                    }}
                  />
                )
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                  <span className="text-white text-lg font-bold">
                    {user?.nickname?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
              )}
            </div>
            {/* Online status indicator */}
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-800"></div>
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold text-sm">{user?.name || user?.nickname || 'Utilizator'}</div>
            <div className="text-gray-400 text-xs">Standard</div>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="py-2">
        {/* New Room */}
        <button
          onClick={() => {
            // Implementăm funcția pentru new room
            onClose();
          }}
          className="w-full px-4 py-3 flex items-center space-x-3 hover:bg-white/5 mobile-transition text-left"
        >
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="text-gray-300 text-sm">Cameră Nouă</span>
        </button>

        {/* VONEX Community */}
        <button
          onClick={() => {
            // Implementăm funcția pentru community
            onClose();
          }}
          className="w-full px-4 py-3 flex items-center space-x-3 hover:bg-white/5 mobile-transition text-left"
        >
          <div className="w-5 h-5 rounded bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
            <span className="text-white text-xs font-bold">V</span>
          </div>
          <span className="text-green-400 text-sm">VONEX Community</span>
        </button>

        <div className="border-t border-white/10 my-2"></div>

        {/* Account Settings */}
        <button
          onClick={() => {
            onAccountSettings();
            onClose();
          }}
          className="w-full px-4 py-3 flex items-center space-x-3 hover:bg-white/5 mobile-transition text-left"
        >
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-gray-300 text-sm">Account Settings</span>
        </button>

        {/* Help Center */}
        <button
          onClick={() => {
            // Implementăm funcția pentru help
            onClose();
          }}
          className="w-full px-4 py-3 flex items-center space-x-3 hover:bg-white/5 mobile-transition text-left"
        >
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-gray-300 text-sm">Help Center</span>
        </button>

        {/* Join VONEX Community */}
        <button
          onClick={() => {
            // Implementăm funcția pentru community join
            window.open('https://discord.gg/vonex', '_blank');
            onClose();
          }}
          className="w-full px-4 py-3 flex items-center space-x-3 hover:bg-white/5 mobile-transition text-left"
        >
          <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0002 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9554 2.4189-2.1568 2.4189Z"/>
          </svg>
          <span className="text-gray-300 text-sm">Join VONEX Discord</span>
        </button>

        <div className="border-t border-white/10 my-2"></div>

        {/* Logout */}
        <button
          onClick={() => {
            onLogout();
            onClose();
          }}
          className="w-full px-4 py-3 flex items-center space-x-3 hover:bg-red-500/10 mobile-transition text-left"
        >
          <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span className="text-red-400 text-sm">Logout</span>
        </button>
      </div>
    </div>
  );
};
// Floating Action Button Component - Bulă albastră rotundă în dreapta jos ca pe X/Twitter
const FloatingPostButton = ({ onCreatePost, opacity = 1 }) => {
  return (
    <div 
      className="mobile-fab transition-all duration-300"
      style={{ opacity: opacity }}
    >
      <button
        onClick={onCreatePost}
        className="w-14 h-14 bg-gradient-to-r from-cyan-500/90 to-blue-500/90 hover:from-cyan-600/90 hover:to-blue-600/90 backdrop-blur-sm text-white rounded-full shadow-lg hover:shadow-xl mobile-transition hover:scale-105 flex items-center justify-center android-ripple touch-target"
        title="Creează postare nouă"
        aria-label="Creează postare nouă"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  );
};

// Post Creation Modal Component - Mobile Optimized
const PostCreationModal = ({ 
  isOpen, 
  onClose, 
  newPost,
  setNewPost,
  handleImageUpload,
  createPost,
  uploadedImages,
  removeImage,
  linkPreview,
  isLoadingPreview
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="mobile-modal bg-gray-800/95 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl w-full max-w-lg overflow-hidden android-viewport-fix">
        {/* Modal Header - iOS Safe Area */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 mobile-header">
          <h2 className="text-white text-xl font-semibold">Postare nouă</h2>
          <button
            onClick={onClose}
            className="touch-target text-gray-400 hover:text-white p-2 rounded-full hover:bg-gray-700/50 mobile-transition mobile-focus"
            aria-label="Închide modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content - Mobile Optimized Scroll */}
        <div className="p-6 max-h-96 overflow-y-auto mobile-scroll">
          <div className="space-y-4">
            {/* Expandable Messenger Input - Mobile Enhanced */}
            <div className="mobile-input-container">
              <ExpandableMessengerInput
                value={newPost}
                onChange={setNewPost}
                onImageUpload={handleImageUpload}
                onSubmit={async () => {
                  await createPost();
                  onClose();
                }}
                placeholder="Ce vrei să împarți cu lumea?"
                maxWords={100}
                showCharCount={true}
              />
            </div>

            {/* Uploaded Images Preview - Mobile Grid */}
            {uploadedImages.length > 0 && (
              <div className="space-y-3">
                <div className="text-sm text-gray-400">
                  {uploadedImages.length} imagine{uploadedImages.length > 1 ? 'i' : ''} atașat{uploadedImages.length > 1 ? 'e' : 'ă'}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {uploadedImages.map((image) => (
                    <div key={image.id} className="relative group mobile-card">
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${image.thumbnail_url}`}
                        alt="Preview"
                        className="w-full h-32 object-cover rounded-lg retina-optimized"
                      />
                      <button
                        onClick={() => removeImage(image.id)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs hover:bg-red-600 mobile-transition opacity-0 group-hover:opacity-100 touch-target"
                        aria-label="Șterge imagine"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Link Preview Loading - Mobile Friendly */}
            {isLoadingPreview && (
              <div className="flex items-center space-x-2 text-gray-400 text-sm p-3 bg-gray-700/30 rounded-lg">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-400"></div>
                <span>Se generează preview pentru link...</span>
              </div>
            )}

            {/* Link Preview - Mobile Enhanced */}
            {linkPreview && (
              <div className="mobile-card bg-gray-700/30 border border-white/10 rounded-xl overflow-hidden">
                {linkPreview.image_url && (
                  <img 
                    src={linkPreview.image_url} 
                    alt="Link preview"
                    className="w-full h-32 object-cover retina-optimized"
                  />
                )}
                <div className="p-4 space-y-2">
                  <div className="text-xs text-gray-400 uppercase">{linkPreview.domain}</div>
                  {linkPreview.title && (
                    <div className="text-white font-medium text-sm line-clamp-2">{linkPreview.title}</div>
                  )}
                  {linkPreview.description && (
                    <div className="text-gray-300 text-sm line-clamp-2">{linkPreview.description}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer - iOS Safe Area Bottom */}
        <div className="p-6 border-t border-white/10 ios-safe-area-bottom">
          <div className="mobile-button-group flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg mobile-transition touch-target mobile-focus android-ripple"
            >
              Anulează
            </button>
            <button
              onClick={async () => {
                await createPost();
                onClose();
              }}
              disabled={!newPost.trim() && uploadedImages.length === 0}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:from-gray-600 disabled:to-gray-700 text-white rounded-lg mobile-transition disabled:cursor-not-allowed touch-target mobile-focus android-ripple"
            >
              Postează
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// Expandable Messenger Input Component pentru World Chat - Facebook-style
const ExpandableMessengerInput = ({ 
  value, 
  onChange, 
  onImageUpload, 
  onSubmit, 
  onVoiceMessage,
  placeholder = "Scrie o postare...", 
  maxWords = 100,
  showCharCount = true
}) => {
  const {
    capturePhoto,
    selectFromGallery,
    startVoiceRecording,
    stopVoiceRecording,
    isRecording
  } = useMessengerInput();

  const [localValue, setLocalValue] = useState(value || '');
  const [isExpanded, setIsExpanded] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [showIcons, setShowIcons] = useState(true); // Pentru controlul vizibilității iconițelor
  const textareaRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  // Auto-resize textarea și count words
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 24 * 6; // 6 rânduri (24px per rând aproximativ)
      textareaRef.current.style.height = Math.min(scrollHeight, maxHeight) + 'px';
      
      // Expand dacă conținutul necesită mai mult de 1 rând
      const shouldExpand = scrollHeight > 48; // ~2 rânduri
      setIsExpanded(shouldExpand);
    }

    // Count words
    const words = localValue.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);

    // Control iconițe - ascunde când utilizatorul tastează
    setShowIcons(localValue.trim() === '');
  }, [localValue]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const words = newValue.trim().split(/\s+/).filter(word => word.length > 0);
    
    if (words.length <= maxWords || newValue.trim() === '') {
      setLocalValue(newValue);
      onChange(newValue);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleCameraClick = async () => {
    console.log('Camera button clicked');
    try {
      const photoFile = await capturePhoto();
      console.log('Photo captured:', photoFile);
      if (photoFile && onImageUpload) {
        const event = {
          target: {
            files: [photoFile]
          }
        };
        console.log('Calling onImageUpload with photo');
        onImageUpload(event);
      } else {
        console.log('No photo captured or no onImageUpload function');
      }
    } catch (error) {
      console.error('Error in handleCameraClick:', error);
      alert('Eroare la accesarea camerei: ' + error.message);
    }
  };

  const handleGalleryClick = async () => {
    console.log('Gallery button clicked');
    try {
      const files = await selectFromGallery();
      console.log('Files selected from gallery:', files);
      if (files && files.length > 0 && onImageUpload) {
        const event = {
          target: {
            files: files
          }
        };
        console.log('Calling onImageUpload with files:', files.length);
        onImageUpload(event);
      } else {
        console.log('No files selected or no onImageUpload function');
      }
    } catch (error) {
      console.error('Error in handleGalleryClick:', error);
      alert('Eroare la selectarea fișierelor: ' + error.message);
    }
  };

  const handleVoiceClick = async () => {
    if (isRecording) {
      stopVoiceRecording();
      setTimeout(() => {
        if (window.lastVoiceFile && onVoiceMessage) {
          onVoiceMessage(window.lastVoiceFile);
          window.lastVoiceFile = null;
        }
      }, 100);
    } else {
      await startVoiceRecording();
    }
  };

  const handleCompressClick = () => {
    setIsExpanded(false);
    if (textareaRef.current) {
      textareaRef.current.style.height = '48px'; // Reset la înălțimea minimă
    }
  };

  const toggleIcons = () => {
    setShowIcons(!showIcons);
  };

  return (
    <div className="relative">
      {/* Main Input Container - Facebook Messenger Style */}
      <div className={`flex bg-gray-700/50 border border-white/20 rounded-2xl px-4 py-3 focus-within:ring-2 focus-within:ring-cyan-400/50 focus-within:border-transparent mobile-transition ${
        isExpanded ? 'items-start' : 'items-center'
      }`}>
        
        {/* Left Side Icons - Cu animație de glisare */}
        <div className={`flex items-center mr-3 overflow-hidden transition-all duration-300 ease-in-out ${
          showIcons ? 'max-w-40 opacity-100' : 'max-w-0 opacity-0'
        } ${isExpanded ? 'items-start pt-1' : 'items-center'}`}>
          <div className="flex items-center space-x-2 flex-shrink-0">
            {/* Camera Icon */}
            <button
              type="button"
              onClick={handleCameraClick}
              className="touch-target p-2 text-gray-400 hover:text-cyan-400 hover:bg-gray-600/50 rounded-full mobile-transition mobile-focus android-ripple"
              title="Fă o poză"
              aria-label="Fă o poză cu camera"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>

            {/* Gallery Icon */}
            <button
              type="button"
              onClick={handleGalleryClick}
              className="touch-target p-2 text-gray-400 hover:text-cyan-400 hover:bg-gray-600/50 rounded-full mobile-transition mobile-focus android-ripple"
              title="Alege din galerie"
              aria-label="Alege imagini din galerie"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>

            {/* Voice Icon */}
            <button
              type="button"
              onClick={handleVoiceClick}
              className={`touch-target p-2 rounded-full mobile-transition mobile-focus android-ripple ${
                isRecording 
                  ? 'text-red-400 bg-red-500/20 animate-pulse' 
                  : 'text-gray-400 hover:text-cyan-400 hover:bg-gray-600/50'
              }`}
              title={isRecording ? "Oprește înregistrarea" : "Înregistrează mesaj vocal"}
              aria-label={isRecording ? "Oprește înregistrarea vocală" : "Înregistrează mesaj vocal"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isRecording ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Săgeata pentru redeschiderea iconițelor - apare când sunt ascunse */}
        {!showIcons && (
          <div className={`flex items-center mr-3 transition-all duration-300 ease-in-out ${
            isExpanded ? 'items-start pt-1' : 'items-center'
          }`}>
            <button
              type="button"
              onClick={toggleIcons}
              className="touch-target p-2 text-gray-400 hover:text-cyan-400 hover:bg-gray-600/50 rounded-full mobile-transition mobile-focus android-ripple"
              title="Afișează opțiuni"
              aria-label="Afișează opțiunile de cameră și galerie"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Compress Button - Doar când e expandat */}
        {isExpanded && (
          <div className="flex items-start mr-3 pt-1">
            <button
              type="button"
              onClick={handleCompressClick}
              className="touch-target p-2 text-gray-400 hover:text-cyan-400 hover:bg-gray-600/50 rounded-full mobile-transition mobile-focus android-ripple"
              title="Comprimă câmpul"
              aria-label="Comprimă câmpul de scriere"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7-7m0 0l-7 7m7-7v18" />
              </svg>
            </button>
          </div>
        )}

        {/* Text Input - Expandable Textarea */}
        <textarea
          ref={textareaRef}
          value={localValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-white placeholder-gray-400 border-none outline-none resize-none mobile-input ios-input mobile-focus overflow-hidden"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          rows={1}
          style={{ minHeight: '24px', maxHeight: '144px' }} // 1-6 rânduri
        />

        {/* Send Button - Touch Optimized */}
        <button
          type="button"
          onClick={onSubmit}
          disabled={!localValue.trim()}
          className={`ml-3 touch-target p-2 rounded-full mobile-transition mobile-focus android-ripple ${
            localValue.trim()
              ? 'text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20'
              : 'text-gray-500 cursor-not-allowed'
          } ${isExpanded ? 'self-end' : ''}`}
          title="Trimite postare"
          aria-label="Trimite postare"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </div>

      {/* Word Count - Mobile Optimized */}
      {showCharCount && (
        <div className="flex justify-between items-center mt-2 px-3">
          <div className={`text-xs ${wordCount > maxWords * 0.9 ? 'text-amber-400' : wordCount > maxWords * 0.8 ? 'text-yellow-400' : 'text-gray-500'}`}>
            {wordCount}/{maxWords} cuvinte
          </div>
          {isRecording && (
            <div className="text-xs text-red-400 animate-pulse flex items-center space-x-1">
              <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
              <span>Înregistrează...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
const MessengerInput = ({ 
  value, 
  onChange, 
  onImageUpload, 
  onSubmit, 
  onVoiceMessage,
  placeholder = "Scrie un mesaj...", 
  maxLength = 500,
  showCharCount = false
}) => {
  const {
    capturePhoto,
    selectFromGallery,
    startVoiceRecording,
    stopVoiceRecording,
    isRecording
  } = useMessengerInput();

  const [localValue, setLocalValue] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      setLocalValue(newValue);
      onChange(newValue);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleCameraClick = async () => {
    console.log('Camera button clicked');
    try {
      const photoFile = await capturePhoto();
      console.log('Photo captured:', photoFile);
      if (photoFile && onImageUpload) {
        const event = {
          target: {
            files: [photoFile]
          }
        };
        console.log('Calling onImageUpload with photo');
        onImageUpload(event);
      } else {
        console.log('No photo captured or no onImageUpload function');
      }
    } catch (error) {
      console.error('Error in handleCameraClick:', error);
      alert('Eroare la accesarea camerei: ' + error.message);
    }
  };

  const handleGalleryClick = async () => {
    console.log('Gallery button clicked');
    try {
      const files = await selectFromGallery();
      console.log('Files selected from gallery:', files);
      if (files && files.length > 0 && onImageUpload) {
        const event = {
          target: {
            files: files
          }
        };
        console.log('Calling onImageUpload with files:', files.length);
        onImageUpload(event);
      } else {
        console.log('No files selected or no onImageUpload function');
      }
    } catch (error) {
      console.error('Error in handleGalleryClick:', error);
      alert('Eroare la selectarea fișierelor: ' + error.message);
    }
  };

  const handleVoiceClick = async () => {
    if (isRecording) {
      stopVoiceRecording();
      setTimeout(() => {
        if (window.lastVoiceFile && onVoiceMessage) {
          onVoiceMessage(window.lastVoiceFile);
          window.lastVoiceFile = null;
        }
      }, 100);
    } else {
      await startVoiceRecording();
    }
  };

  return (
    <div className="relative">
      {/* Main Input Container - Mobile Enhanced */}
      <div className="flex items-center bg-gray-700/50 border border-white/20 rounded-full px-3 py-2 focus-within:ring-2 focus-within:ring-cyan-400/50 focus-within:border-transparent mobile-transition">
        
        {/* Left Side Icons - Touch Optimized */}
        <div className="flex items-center space-x-1 mr-3">
          {/* Camera Icon */}
          <button
            type="button"
            onClick={handleCameraClick}
            className="touch-target p-2 text-gray-400 hover:text-cyan-400 hover:bg-gray-600/50 rounded-full mobile-transition mobile-focus android-ripple"
            title="Fă o poză"
            aria-label="Fă o poză cu camera"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>

          {/* Gallery Icon */}
          <button
            type="button"
            onClick={handleGalleryClick}
            className="touch-target p-2 text-gray-400 hover:text-cyan-400 hover:bg-gray-600/50 rounded-full mobile-transition mobile-focus android-ripple"
            title="Alege din galerie"
            aria-label="Alege imagini din galerie"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>

          {/* Voice Icon */}
          <button
            type="button"
            onClick={handleVoiceClick}
            className={`touch-target p-2 rounded-full mobile-transition mobile-focus android-ripple ${
              isRecording 
                ? 'text-red-400 bg-red-500/20 animate-pulse' 
                : 'text-gray-400 hover:text-cyan-400 hover:bg-gray-600/50'
            }`}
            title={isRecording ? "Oprește înregistrarea" : "Înregistrează mesaj vocal"}
            aria-label={isRecording ? "Oprește înregistrarea vocală" : "Înregistrează mesaj vocal"}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isRecording ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              )}
            </svg>
          </button>
        </div>

        {/* Text Input - Mobile Input Optimized */}
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-white placeholder-gray-400 border-none outline-none mobile-input ios-input mobile-focus"
          maxLength={maxLength}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
        />

        {/* Send Button - Touch Optimized */}
        <button
          type="button"
          onClick={onSubmit}
          disabled={!localValue.trim()}
          className={`ml-3 touch-target p-2 rounded-full mobile-transition mobile-focus android-ripple ${
            localValue.trim()
              ? 'text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20'
              : 'text-gray-500 cursor-not-allowed'
          }`}
          title="Trimite mesaj"
          aria-label="Trimite mesaj"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </div>

      {/* Character Count - Mobile Optimized */}
      {showCharCount && (
        <div className="flex justify-between items-center mt-2 px-3">
          <div className="text-xs text-gray-500">
            {localValue.length}/{maxLength} caractere
          </div>
          {isRecording && (
            <div className="text-xs text-red-400 animate-pulse flex items-center space-x-1">
              <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
              <span>Înregistrează...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Replace axios with fetch
const api = {
  get: async (url, options = {}) => {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return { data: await response.json() };
  },
  post: async (url, data, options = {}) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return { data: await response.json() };
  },
  put: async (url, data, options = {}) => {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return { data: await response.json() };
  },
  delete: async (url, options = {}) => {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return { data: await response.json() };
  }
};

// Language Context
const LanguageContext = createContext();

const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem('vonex_language') || 'en'
  );

  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode);
    localStorage.setItem('vonex_language', langCode);
  };

  const t = (key, variables = {}) => getTranslation(key, currentLanguage, variables);

  return (
    <LanguageContext.Provider value={{ currentLanguage, changeLanguage, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const { changeLanguage } = useLanguage();

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      // Set user's saved language if available
      if (response.data.language) {
        changeLanguage(response.data.language);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post(`${API}/auth/login`, { email, password });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const register = async (email, password, firstName, lastName, nickname) => {
    try {
      const response = await api.post(`${API}/auth/register`, { 
        email, 
        password, 
        first_name: firstName,
        last_name: lastName,
        nickname 
      });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Registration failed' };
    }
  };

  const updateProfile = async (updates) => {
    try {
      const response = await api.put(`${API}/auth/profile`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Update failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      updateProfile,
      logout,
      fetchCurrentUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Account Settings Component
const AccountSettings = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    email: '',
    avatar_url: null
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [nicknameRequestData, setNicknameRequestData] = useState({
    newNickname: '',
    reason: ''
  });
  const [nicknameRequests, setNicknameRequests] = useState([]);
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [showAvatarSection, setShowAvatarSection] = useState(false);
  const fileInputRef = useRef(null);
  const { user, updateProfile, token } = useAuth();
  const { t, currentLanguage, changeLanguage, languages } = useLanguage();

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        nickname: user.nickname || '',
        email: user.email || '',
        avatar_url: user.avatar_url || null
      });
      setSelectedAvatar(user.avatar_url || null);
    }
    fetchNicknameRequests();
  }, [user]);

  const fetchNicknameRequests = async () => {
    try {
      const response = await api.get(`${API}/auth/nickname-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNicknameRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch nickname requests:', error);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: t('invalidFileType', 'Please select a valid image file') });
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: t('fileTooLarge', 'File size must be less than 2MB') });
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedAvatar(e.target.result);
      setShowAvatarSection(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDefaultAvatarSelect = (avatarId) => {
    const avatar = defaultAvatars.find(a => a.id === avatarId);
    if (avatar) {
      setSelectedAvatar(avatarId);
      setShowAvatarSection(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    
    try {
      const updates = {
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        language: currentLanguage,
        avatar_url: selectedAvatar
      };
      
      const result = await updateProfile(updates);
      if (result.success) {
        setMessage({ type: 'success', text: t('profileUpdated') });
        setProfileData(prev => ({ ...prev, avatar_url: selectedAvatar }));
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleNicknameRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    
    try {
      const response = await api.post(`${API}/auth/nickname-request`, {
        new_nickname: nicknameRequestData.newNickname,
        reason: nicknameRequestData.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setMessage({ type: 'success', text: t('nicknameRequestSubmitted') });
        setNicknameRequestData({ newNickname: '', reason: '' });
        setShowNicknameModal(false);
        fetchNicknameRequests();
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || t('nicknameAlreadyTaken') });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: t('passwordMismatch', 'Passwords do not match') });
      return;
    }
    
    setLoading(true);
    setMessage('');
    
    try {
      const response = await api.post(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setMessage({ type: 'success', text: t('passwordChanged', 'Password changed successfully') });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', icon: '👤', label: t('profile') },
    { id: 'preferences', icon: '⚙️', label: t('preferences') },
    { id: 'security', icon: '🔒', label: t('security') },
    { id: 'about', icon: 'ℹ️', label: t('about') }
  ];

  return (
    <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800/95 to-gray-900/95 backdrop-blur-xl px-6 py-4 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="text-gray-400 hover:text-white transition-all duration-200 p-2 rounded-lg hover:bg-white/10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-white text-xl font-bold">{t('accountSettings')}</h1>
            <p className="text-gray-400 text-sm">{user?.email}</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-64 bg-gray-800/50 backdrop-blur-sm border-r border-white/10 p-4">
          <nav className="space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                <span className="text-lg">{tab.icon}</span>
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {message && (
            <div className={`mb-6 p-4 rounded-xl backdrop-blur-sm ${
              message.type === 'success' 
                ? 'bg-green-500/20 border border-green-500/50 text-green-200'
                : 'bg-red-500/20 border border-red-500/50 text-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{t('editProfile')}</h2>
                <p className="text-gray-400">{t('updateProfileInfo', 'Update your profile information')}</p>
              </div>
              
              <form onSubmit={handleProfileUpdate} className="space-y-6">
                {/* Avatar Section */}
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-4">{t('avatar')}</label>
                  <div className="flex items-center space-x-6">
                    {/* Current Avatar Display */}
                    <div className="relative">
                      {selectedAvatar ? (
                        <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-purple-500 shadow-lg">
                          {selectedAvatar.startsWith('data:') ? (
                            <img src={selectedAvatar} alt="Avatar" className="w-full h-full object-cover" />
                          ) : (
                            <div 
                              className="w-full h-full"
                              dangerouslySetInnerHTML={{ 
                                __html: defaultAvatars.find(a => a.id === selectedAvatar)?.svg || '' 
                              }}
                            />
                          )}
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center border-4 border-gray-500 shadow-lg">
                          <span className="text-white text-2xl font-bold">
                            {user?.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Avatar Change Buttons */}
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={() => setShowAvatarSection(true)}
                        className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm"
                      >
                        {t('changeAvatar')}
                      </button>
                      {selectedAvatar && (
                        <button
                          type="button"
                          onClick={() => setSelectedAvatar(null)}
                          className="block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm"
                        >
                          {t('removeAvatar')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Avatar Selection Modal */}
                {showAvatarSection && (
                  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-xl rounded-3xl p-6 w-full max-w-2xl border border-white/20 shadow-2xl max-h-[80vh] overflow-y-auto">
                      <div className="text-center mb-6">
                        <h3 className="text-white text-xl font-bold mb-2">{t('chooseAvatar')}</h3>
                        <p className="text-gray-300 text-sm">{t('selectDefaultAvatar')}</p>
                      </div>

                      {/* Default Avatars Grid */}
                      <div className="mb-8">
                        <h4 className="text-white font-semibold mb-4">{t('defaultAvatars')}</h4>
                        <div className="grid grid-cols-5 gap-4">
                          {defaultAvatars.map((avatar) => (
                            <button
                              key={avatar.id}
                              onClick={() => handleDefaultAvatarSelect(avatar.id)}
                              className={`relative group transition-all duration-200 ${
                                selectedAvatar === avatar.id ? 'ring-4 ring-purple-500 rounded-full' : ''
                              }`}
                            >
                              <div 
                                className="w-16 h-16 rounded-full overflow-hidden hover:scale-110 transition-transform duration-200 shadow-lg"
                                dangerouslySetInnerHTML={{ __html: avatar.svg }}
                              />
                              <div className="absolute inset-0 bg-black/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Custom Upload Section */}
                      <div className="mb-6">
                        <h4 className="text-white font-semibold mb-4">{t('uploadCustomAvatar')}</h4>
                        <div className="border-2 border-dashed border-gray-600 rounded-xl p-6 text-center">
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileUpload}
                            className="hidden"
                          />
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl transition-all duration-200 font-medium"
                          >
                            <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            {t('uploadImage')}
                          </button>
                          <p className="text-gray-400 text-xs mt-2">{t('supportedFormats')}</p>
                        </div>
                      </div>

                      {/* Modal Close Button */}
                      <div className="flex justify-center">
                        <button
                          onClick={() => setShowAvatarSection(false)}
                          className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-6 py-2 rounded-lg transition-all duration-200 font-medium border border-white/20"
                        >
                          {t('close')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Nickname Change Request Modal */}
                {showNicknameModal && (
                  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-xl rounded-3xl p-6 w-full max-w-md border border-white/20 shadow-2xl">
                      <div className="text-center mb-6">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </div>
                        <h3 className="text-white text-xl font-bold mb-2">{t('requestNicknameChange')}</h3>
                        <p className="text-gray-300 text-sm">{t('nicknameChangeExplanation', 'Nickname changes require admin approval')}</p>
                      </div>

                      <form onSubmit={handleNicknameRequest} className="space-y-4">
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">{t('currentNickname')}</label>
                          <input
                            type="text"
                            value={profileData.nickname}
                            disabled
                            className="w-full bg-gray-700/50 text-gray-400 px-4 py-3 rounded-xl border border-white/10 cursor-not-allowed"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">{t('newNickname')}</label>
                          <input
                            type="text"
                            value={nicknameRequestData.newNickname}
                            onChange={(e) => setNicknameRequestData({...nicknameRequestData, newNickname: e.target.value})}
                            className="w-full bg-white/10 backdrop-blur-sm text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 border border-white/20"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-gray-300 text-sm font-medium mb-2">{t('reasonForChange')}</label>
                          <textarea
                            value={nicknameRequestData.reason}
                            onChange={(e) => setNicknameRequestData({...nicknameRequestData, reason: e.target.value})}
                            placeholder={t('reasonPlaceholder')}
                            className="w-full bg-white/10 backdrop-blur-sm text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 border border-white/20 resize-none"
                            rows="3"
                            required
                          />
                        </div>

                        <div className="flex space-x-3 pt-4">
                          <button
                            type="button"
                            onClick={() => setShowNicknameModal(false)}
                            className="flex-1 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white py-3 rounded-xl transition-all duration-200 font-medium border border-white/20"
                          >
                            {t('cancel')}
                          </button>
                          <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white py-3 rounded-xl transition-all duration-200 font-medium shadow-lg disabled:opacity-50"
                          >
                            {loading ? t('pleaseWait') : t('submitRequest')}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">{t('firstName')}</label>
                    <input
                      type="text"
                      value={profileData.firstName}
                      onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                      className="w-full bg-white/10 backdrop-blur-sm text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 border border-white/20"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">{t('lastName')}</label>
                    <input
                      type="text"
                      value={profileData.lastName}
                      onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                      className="w-full bg-white/10 backdrop-blur-sm text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 border border-white/20"
                      required
                    />
                  </div>
                </div>

                {/* Nickname Section - Read Only with Change Request */}
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">{t('nickname')}</label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="text"
                      value={profileData.nickname}
                      disabled
                      className="flex-1 bg-gray-700/50 text-gray-400 px-4 py-3 rounded-xl border border-white/10 cursor-not-allowed"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNicknameModal(true)}
                      className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-4 py-3 rounded-xl transition-all duration-200 font-medium text-sm"
                    >
                      {t('requestNicknameChange')}
                    </button>
                  </div>
                  <p className="text-gray-500 text-xs mt-1">{t('nicknameVisibleInChat', 'This nickname is visible in chat messages')}</p>
                  
                  {/* Show recent nickname requests */}
                  {nicknameRequests.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-gray-300 font-medium mb-2">{t('nicknameChangeRequests')}</h4>
                      <div className="space-y-2">
                        {nicknameRequests.slice(0, 3).map((request) => (
                          <div key={request.id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-300 text-sm">{request.new_nickname}</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                request.status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                                request.status === 'approved' ? 'bg-green-500/20 text-green-300' :
                                'bg-red-500/20 text-red-300'
                              }`}>
                                {t(`${request.status}Request`)}
                              </span>
                            </div>
                            {request.admin_comment && (
                              <p className="text-gray-400 text-xs mt-1">{request.admin_comment}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">{t('emailAddress')}</label>
                  <input
                    type="email"
                    value={profileData.email}
                    disabled
                    className="w-full bg-gray-700/50 text-gray-400 px-4 py-3 rounded-xl border border-white/10 cursor-not-allowed"
                  />
                  <p className="text-gray-500 text-xs mt-1">{t('emailCannotBeChanged', 'Email address cannot be changed')}</p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-xl transition-all duration-200 font-semibold disabled:opacity-50"
                >
                  {loading ? t('pleaseWait') : t('saveChanges')}
                </button>
              </form>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{t('preferences')}</h2>
                <p className="text-gray-400">{t('customizeExperience', 'Customize your VONEX experience')}</p>
              </div>

              <div className="space-y-6">
                {/* Language Selection */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">{t('language')}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(languages).map(([code, lang]) => (
                      <button
                        key={code}
                        onClick={() => changeLanguage(code)}
                        className={`flex items-center space-x-3 p-3 rounded-xl transition-all duration-200 ${
                          currentLanguage === code
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'
                        }`}
                      >
                        <span className="text-xl">{lang.flag}</span>
                        <span className="font-medium">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{t('security')}</h2>
                <p className="text-gray-400">{t('manageAccountSecurity', 'Manage your account security settings')}</p>
              </div>

              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">{t('currentPassword')}</label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    className="w-full bg-white/10 backdrop-blur-sm text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 border border-white/20"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">{t('newPassword')}</label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    className="w-full bg-white/10 backdrop-blur-sm text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 border border-white/20"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">{t('confirmPassword')}</label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    className="w-full bg-white/10 backdrop-blur-sm text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 border border-white/20"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-xl transition-all duration-200 font-semibold disabled:opacity-50"
                >
                  {loading ? t('pleaseWait') : t('changePassword')}
                </button>
              </form>
            </div>
          )}

          {/* About Tab */}
          {activeTab === 'about' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{t('about')}</h2>
                <p className="text-gray-400">{t('aboutVonex', 'About VONEX and your account')}</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white">{t('appName')}</h3>
                  <p className="text-gray-300">{t('appDescription')}</p>
                  <p className="text-gray-400 text-sm">
                    {t('version', 'Version')} 1.0.0
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Components
const LoginForm = ({ isLogin, onToggle }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nickname, setNickname] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, register } = useAuth();
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = isLogin 
      ? await login(email, password)
      : await register(email, password, firstName, lastName, nickname);

    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-indigo-500/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-8 w-full max-w-md border border-white/20 shadow-2xl">
        <div className="text-center mb-8">
          {/* Logo VONEX original cu 100% mai mare pentru login */}
          <div className="w-auto h-40 mx-auto mb-3 flex items-center justify-center">
            <img 
              src="/vonex-original-logo.png" 
              alt="VONEX" 
              className="h-32 w-auto object-contain"
              style={{
                filter: 'drop-shadow(0 0 20px rgba(0, 255, 255, 0.6)) drop-shadow(0 0 30px rgba(68, 255, 68, 0.5)) drop-shadow(0 0 40px rgba(255, 255, 68, 0.4)) drop-shadow(0 0 50px rgba(255, 136, 68, 0.3))'
              }}
            />
          </div>
          
          {/* Slogan VONEX - pe o singură linie, încadrat perfect stânga-dreapta */}
          <div className="mb-4 px-6">
            <p className="text-sm font-bold mb-3 text-white text-center whitespace-nowrap" style={{
              fontFamily: 'monospace',
              letterSpacing: '0.01em'
            }}>
              Voice • Opinions • Network • Experiences
            </p>
          </div>
          
          <p className="text-gray-300 text-base mb-6">
            {isLogin ? t('signInSubtitle') : t('createAccountSubtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!isLogin && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-gray-300 text-sm font-medium">{t('firstName')}</label>
                  <input
                    type="text"
                    placeholder={t('firstNamePlaceholder')}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white/20 transition-all duration-200"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-gray-300 text-sm font-medium">{t('lastName')}</label>
                  <input
                    type="text"
                    placeholder={t('lastNamePlaceholder')}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white/20 transition-all duration-200"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-gray-300 text-sm font-medium">{t('nickname')}</label>
                <input
                  type="text"
                  placeholder={t('nicknamePlaceholder')}
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="w-full px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white/20 transition-all duration-200"
                  required
                />
                <p className="text-gray-400 text-xs">{t('nicknameWillAppearInChat', 'This nickname will appear in chat messages')}</p>
              </div>
            </>
          )}
          
          <div className="space-y-2">
            <label className="text-gray-300 text-sm font-medium">{t('emailAddress')}</label>
            <input
              type="email"
              placeholder={t('emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white/20 transition-all duration-200"
              required
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-gray-300 text-sm font-medium">{t('password')}</label>
            <input
              type="password"
              placeholder={t('passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white/20 transition-all duration-200"
              required
            />
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 text-red-200 text-sm backdrop-blur-sm">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-4 px-4 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{t('pleaseWait')}</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                <span>{isLogin ? t('signIn') : t('createAccount')}</span>
              </>
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/20"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-gray-300">
                {isLogin ? t('noAccountYet') : t('alreadyHaveAccount')}
              </span>
            </div>
          </div>
          <button
            onClick={onToggle}
            className="mt-4 text-purple-400 hover:text-purple-300 font-semibold transition-colors text-lg"
          >
            {isLogin ? t('registerHere') : t('signInHere')}
          </button>
        </div>
      </div>
    </div>
  );
};

const ChatRoom = ({ room, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [ws, setWs] = useState(null);
  const [isPolling, setIsPolling] = useState(true);
  const [roomUsers, setRoomUsers] = useState([]);
  const [showUserList, setShowUserList] = useState(false);
  const [showPrivateChat, setShowPrivateChat] = useState(false);
  const [privateChatUser, setPrivateChatUser] = useState(null);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [newPrivateMessage, setNewPrivateMessage] = useState('');
  const [showAvatarBubble, setShowAvatarBubble] = useState(null);
  const [avatarBubblePosition, setAvatarBubblePosition] = useState({ x: 0, y: 0 });
  const [isPrivateChatUserFriend, setIsPrivateChatUserFriend] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [userScrolled, setUserScrolled] = useState(false);
  const [permanentButtons, setPermanentButtons] = useState(new Set()); // Track which messages have permanent buttons
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollingInterval = useRef(null);
  const { token, user } = useAuth();

  const scrollToBottom = (force = false) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      setIsAtBottom(true);
      setShowScrollToBottom(false);
      if (force) {
        setUserScrolled(false);
      }
    }
  };

  // Check if user is at bottom of chat
  const checkIfAtBottom = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const atBottom = scrollHeight - scrollTop - clientHeight < 50; // 50px threshold
      setIsAtBottom(atBottom);
      setShowScrollToBottom(!atBottom);
    }
  };

  // Handle scroll events
  const handleScroll = () => {
    setUserScrolled(true);
    checkIfAtBottom();
  };

  // Only auto-scroll to bottom when:
  // 1. User sends a new message
  // 2. User is at bottom and new messages arrive
  // 3. Initial load
  useEffect(() => {
    if (!userScrolled || isAtBottom) {
      scrollToBottom();
    }
  }, [messages]);

  // Fetch messages function
  const fetchMessages = async () => {
    try {
      const response = await api.get(`${API}/rooms/${room.id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  // Fetch only real friends (not room users)
  const fetchFavorites = async () => {
    try {
      // Get friends list only
      const friendsResponse = await api.get(`${API}/friends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const friends = friendsResponse.data.map(friend => ({
        id: friend.friend_user_id,
        nickname: friend.friend_nickname,
        avatar_url: friend.friend_avatar_url,
        is_friend: true,
        unread_count: 0, // Will be updated by separate call if needed
        first_unread_message_id: null
      }));
      
      setRoomUsers(friends);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    }
  };

  // Remove from favorites
  const removeFromFavorites = async (userId) => {
    console.log('🔥 DEBUGGING: removeFromFavorites called with userId:', userId);
    console.log('🔥 DEBUGGING: Current roomUsers:', roomUsers);
    
    try {
      const userToRemove = roomUsers.find(u => u.id === userId);
      console.log('🔥 DEBUGGING: User to remove:', userToRemove);
      
      // Temporarily skip confirmation for easier testing
      const confirmed = true; // window.confirm(`Elimină ${userToRemove?.nickname || 'utilizatorul'} din favorit?\n\nApasă OK pentru a confirma eliminarea.`);
      console.log('🔥 DEBUGGING: User confirmed (auto-true):', confirmed);
      
      if (confirmed) {
        console.log('🔥 DEBUGGING: Making API call...');
        
        // Call backend to remove friendship
        const response = await api.delete(`${API}/friends/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('🔥 DEBUGGING: API response:', response);
        
        // Remove from local state immediately
        setRoomUsers(prev => {
          const newUsers = prev.filter(user => user.id !== userId);
          console.log('🔥 DEBUGGING: New roomUsers after filter:', newUsers);
          return newUsers;
        });
        
        // Show success message
        alert(`${userToRemove?.nickname || 'Utilizatorul'} a fost eliminat din favorite!`);
        console.log('🔥 DEBUGGING: User removal completed successfully');
      } else {
        console.log('🔥 DEBUGGING: User cancelled the removal');
      }
    } catch (error) {
      console.error('🔥 DEBUGGING: Error in removeFromFavorites:', error);
      
      // If backend call fails, try to remove locally anyway
      const removeLocally = window.confirm('Nu s-a putut elimina din backend. Vrei să elimini doar local?');
      if (removeLocally) {
        setRoomUsers(prev => prev.filter(user => user.id !== userId));
        console.log('🔥 DEBUGGING: Removed locally after backend error');
      }
    }
  };

  // Get last seen time for a user in this room (placeholder - would be stored locally or in backend)
  const getLastSeenTime = (userId) => {
    // For now, use localStorage to track last seen time per user per room
    const key = `last_seen_${room.id}_${userId}`;
    const lastSeen = localStorage.getItem(key);
    return lastSeen ? new Date(lastSeen) : new Date(0); // If no record, show all messages as unread
  };

  // Update last seen time for a user in this room
  const updateLastSeenTime = (userId) => {
    const key = `last_seen_${room.id}_${userId}`;
    localStorage.setItem(key, new Date().toISOString());
  };

  // Fetch private messages
  const fetchPrivateMessages = async (userId) => {
    try {
      const response = await api.get(`${API}/private-messages/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrivateMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch private messages:', error);
    }
  };

  // Send private message
  const sendPrivateMessage = async (e) => {
    e.preventDefault();
    if (!newPrivateMessage.trim() || !privateChatUser) return;

    try {
      const response = await api.post(`${API}/private-messages`, {
        content: newPrivateMessage.trim(),
        recipient_id: privateChatUser.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPrivateMessages(prev => [...prev, response.data]);
      setNewPrivateMessage('');
    } catch (error) {
      console.error('Failed to send private message:', error);
      alert('Nu s-a putut trimite mesajul privat. Încearcă din nou.');
    }
  };

  // Add to favorites (friends)
  const addToFavorites = async (userId) => {
    try {
      await api.post(`${API}/friends/request`, {
        friend_user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update user list to reflect friend status
      setRoomUsers(prev => 
        prev.map(u => u.id === userId ? {...u, is_friend: true} : u)
      );
      
      alert('Utilizator adăugat la favorit!');
    } catch (error) {
      console.error('Failed to add to favorites:', error);
      alert('Nu s-a putut adăuga la favorit.');
    }
  };

  // Check friend status
  const checkFriendStatus = async (userId) => {
    try {
      const response = await api.get(`${API}/friends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const isFriend = response.data.some(friend => friend.friend_user_id === userId);
      setIsPrivateChatUserFriend(isFriend);
    } catch (error) {
      console.error('Failed to check friend status:', error);
      setIsPrivateChatUserFriend(false);
    }
  };

  // Toggle friend status
  const toggleFriendStatus = async () => {
    if (!privateChatUser) return;

    try {
      if (isPrivateChatUserFriend) {
        // Remove from friends - need to implement unfriend endpoint
        alert('Funcționalitatea de eliminare din favorite va fi implementată în curând.');
      } else {
        await api.post(`${API}/friends/request`, {
          friend_user_id: privateChatUser.id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsPrivateChatUserFriend(true);
        alert('Utilizator adăugat la favorite!');
      }
    } catch (error) {
      console.error('Failed to toggle friend status:', error);
      alert('Nu s-a putut actualiza statusul de favorit.');
    }
  };

  // Open private chat
  const openPrivateChat = (user) => {
    setPrivateChatUser(user);
    setShowPrivateChat(true);
    setShowUserList(false);
    setShowAvatarBubble(null);
    fetchPrivateMessages(user.id);
    checkFriendStatus(user.id);
  };

  // Add to favorites from message avatar
  const addToFavoritesFromAvatar = async (userId) => {
    try {
      await api.post(`${API}/friends/request`, {
        friend_user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Utilizator adăugat la favorit!');
    } catch (error) {
      console.error('Failed to add to favorites:', error);
      alert('Nu s-a putut adăuga la favorit.');
    }
  };

  // Open private chat from avatar
  const openPrivateChatFromAvatar = (chatUser) => {
    setPrivateChatUser(chatUser);
    setShowPrivateChat(true);
    setShowAvatarBubble(null);
    fetchPrivateMessages(chatUser.id);
    checkFriendStatus(chatUser.id);
  };

  // Toggle permanent buttons for a message
  const togglePermanentButtons = (messageId) => {
    setPermanentButtons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId); // Hide buttons if already showing
      } else {
        newSet.clear(); // Clear all other permanent buttons
        newSet.add(messageId); // Show buttons for this message only
      }
      return newSet;
    });
  };

  // Handle avatar click to toggle permanent buttons
  const handleAvatarClick = (messageId) => {
    togglePermanentButtons(messageId);
  };

  // Navigate to first unread message from a friend
  const navigateToFirstUnreadMessage = (friendUser) => {
    if (friendUser.first_unread_message_id) {
      // Find the message element and scroll to it
      const messageElement = document.getElementById(`message-${friendUser.first_unread_message_id}`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Highlight the message temporarily
        messageElement.classList.add('bg-yellow-200/20', 'border', 'border-yellow-400/50', 'rounded-lg');
        setTimeout(() => {
          messageElement.classList.remove('bg-yellow-200/20', 'border', 'border-yellow-400/50', 'rounded-lg');
        }, 3000);
      } else {
        // If message not found in current view, reload messages and try again
        fetchMessages().then(() => {
          setTimeout(() => {
            const retryElement = document.getElementById(`message-${friendUser.first_unread_message_id}`);
            if (retryElement) {
              retryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              retryElement.classList.add('bg-yellow-200/20', 'border', 'border-yellow-400/50', 'rounded-lg');
              setTimeout(() => {
                retryElement.classList.remove('bg-yellow-200/20', 'border', 'border-yellow-400/50', 'rounded-lg');
              }, 3000);
            }
          }, 500);
        });
      }
    }
    
    // Update last seen time for this friend
    updateLastSeenTime(friendUser.id);
    // Refresh the friends list to update unread counts
    setTimeout(() => fetchFavorites(), 1000);
    // Close the users panel
    setShowUserList(false);
  };

  useEffect(() => {
    // Initial fetch
    fetchMessages();
    fetchFavorites();
    
    // Initialize scroll detection
    checkIfAtBottom();

    // Try WebSocket connection first
    const wsUrl = `${BACKEND_URL.replace('https:', 'wss:').replace('http:', 'ws:')}/ws/${room.id}`;
    const websocket = new WebSocket(wsUrl);
    let wsConnected = false;

    const wsTimeout = setTimeout(() => {
      if (!wsConnected) {
        console.log('WebSocket connection failed, switching to polling...');
        websocket.close();
        setWs(null);
        startPolling();
      }
    }, 5000); // 5 second timeout

    websocket.onopen = () => {
      console.log('WebSocket connected successfully');
      wsConnected = true;
      clearTimeout(wsTimeout);
      setWs(websocket);
      setIsPolling(false);
      stopPolling();
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.error) {
        console.error('WebSocket error:', data.error);
        return;
      }
      if (data.type === 'message') {
        setMessages(prev => [...prev, {
          id: data.id,
          content: data.content,
          room_id: data.room_id,
          user_id: data.user_id,
          user_name: data.user_name,
          user_avatar: data.user_avatar,
          created_at: data.created_at
        }]);
      }
    };

    websocket.onclose = () => {
      console.log('WebSocket disconnected, switching to polling...');
      setWs(null);
      if (!wsConnected) {
        clearTimeout(wsTimeout);
        startPolling();
      }
    };

    websocket.onerror = () => {
      console.error('WebSocket error, switching to polling...');
      clearTimeout(wsTimeout);
      websocket.close();
      startPolling();
    };

    // Cleanup function
    return () => {
      clearTimeout(wsTimeout);
      websocket.close();
      stopPolling();
    };
  }, [room.id, token]);

  const startPolling = () => {
    if (pollingInterval.current) return; // Already polling
    
    setIsPolling(true);
    pollingInterval.current = setInterval(() => {
      fetchMessages();
      fetchFavorites(); // Also update favorites list
    }, 3000); // Poll every 3 seconds
    console.log('Started message polling (3 second interval)');
  };

  const stopPolling = () => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
      setIsPolling(false);
      console.log('Stopped message polling');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageContent = newMessage.trim();
    
    // Clear input immediately at the start after getting content
    setNewMessage('');
    
    try {
      if (ws && ws.readyState === WebSocket.OPEN) {
        // Send via WebSocket
        ws.send(JSON.stringify({
          content: messageContent,
          token: token
        }));
        // Force scroll to bottom when user sends message
        setTimeout(() => {
          scrollToBottom(true);
        }, 100);
      } else {
        // Send via HTTP API (fallback)
        const response = await api.post(`${API}/rooms/${room.id}/messages`, {
          content: messageContent
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Force scroll to bottom when user sends message
        setTimeout(() => {
          scrollToBottom(true);
        }, 100);
        // Immediately fetch messages to show the new message
        setTimeout(fetchMessages, 500);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Show error to user
      alert('Nu s-a putut trimite mesajul. Încearcă din nou.');
      // Restore the message on error so user can retry
      setNewMessage(messageContent);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800/95 to-gray-900/95 backdrop-blur-xl px-6 py-4 flex items-center justify-between border-b border-white/10 shadow-lg">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="text-gray-400 hover:text-white transition-all duration-200 p-2 rounded-lg hover:bg-white/10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <div className="flex items-center space-x-3">
            {getRoomIcon(room.name, room.is_private)}
            <div>
              <h2 className="text-white font-bold text-xl">{room.name}</h2>
              {room.description && (
                <p className="text-gray-400 text-sm">{room.description}</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className={`flex items-center ${isPolling ? 'text-yellow-400' : 'text-green-400'}`}>
            <div className={`w-2 h-2 ${isPolling ? 'bg-yellow-400' : 'bg-green-400'} rounded-full animate-pulse mr-2`}></div>
            <span className="text-sm font-medium">
              {isPolling ? 'Sincronizare...' : 'Online'}
            </span>
          </div>
          <button
            onClick={() => setShowUserList(!showUserList)}
            className="bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full border border-white/20 hover:bg-white/20 transition-all duration-200 flex items-center space-x-2"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
            <span className="text-white text-sm font-medium">Favoriti</span>
          </button>
        </div>
      </div>

      {/* Messages - Responsive */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-3 sm:space-y-4 relative"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            id={`message-${message.id}`}
            className={`flex ${message.user_id === user?.id ? 'justify-end' : 'justify-start'} w-full relative`}
          >
            <div className="flex flex-col space-y-1 max-w-full relative">
              {/* Avatar and Name Row with Action Buttons - WhatsApp Style */}
              {message.user_id !== user?.id && (
                <div className="flex items-center justify-between mb-1 group">
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <button
                        onClick={() => handleAvatarClick(message.id)}
                        className="w-8 h-8 rounded-full overflow-hidden border-2 border-purple-500/50 hover:border-purple-400 flex-shrink-0 cursor-pointer transition-all duration-200 hover:scale-105"
                      >
                        {message.user_avatar ? (
                          message.user_avatar.startsWith('data:') ? (
                            <img 
                              src={message.user_avatar} 
                              alt="Avatar" 
                              className="w-full h-full object-cover" 
                            />
                          ) : (
                            <div 
                              className="w-full h-full"
                              dangerouslySetInnerHTML={{ 
                                __html: defaultAvatars.find(a => a.id === message.user_avatar)?.svg || '' 
                              }}
                            />
                          )
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                            <span className="text-white text-xs font-semibold">
                              {message.user_name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                        )}
                      </button>
                      
                      {/* Interactive Plus Indicator */}
                      <div className={`absolute -top-0.5 -right-0.5 w-4 h-4 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full border-2 border-white shadow-lg flex items-center justify-center transition-all duration-200 pointer-events-none ${
                        permanentButtons.has(message.id) 
                          ? 'opacity-100 scale-110' 
                          : 'opacity-60 group-hover:opacity-100 group-hover:scale-110'
                      }`}>
                        <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d={
                            permanentButtons.has(message.id) ? "M20 12H4" : "M12 4v16m8-8H4"
                          } />
                        </svg>
                      </div>
                      
                      {/* Subtle pulse animation - only when not permanent */}
                      {!permanentButtons.has(message.id) && (
                        <div className="absolute inset-0 rounded-full border-2 border-purple-400 animate-ping opacity-20 group-hover:opacity-0 pointer-events-none"></div>
                      )}
                    </div>
                    
                    <div className="text-xs font-semibold text-purple-300 opacity-90">
                      {message.user_name}
                    </div>
                  </div>
                  
                  {/* Action Buttons - show on hover OR when permanent */}
                  <div className={`flex space-x-1 transition-all duration-200 flex-shrink-0 ${
                    permanentButtons.has(message.id) 
                      ? 'opacity-100' 
                      : 'opacity-0 group-hover:opacity-100'
                  }`}>
                    <button
                      onClick={() => openPrivateChatFromAvatar({
                        id: message.user_id,
                        nickname: message.user_name,
                        avatar_url: message.user_avatar
                      })}
                      className="bg-blue-600/90 hover:bg-blue-600 text-white px-2 py-1 rounded-lg text-xs transition-colors duration-200 flex items-center space-x-1"
                      title="Chat privat"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <span>Chat</span>
                    </button>
                    <button
                      onClick={() => addToFavoritesFromAvatar(message.user_id)}
                      className="bg-purple-600/90 hover:bg-purple-600 text-white px-2 py-1 rounded-lg text-xs transition-colors duration-200 flex items-center"
                      title="Adaugă la favorit"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                      <span>★</span>
                    </button>
                  </div>
                </div>
              )}
              
              {/* Message Bubble - Clean without action buttons */}
              <div
                className={`px-4 py-3 rounded-2xl shadow-lg backdrop-blur-sm border ${
                  message.user_id === user?.id
                    ? 'bg-gradient-to-br from-purple-600 to-blue-600 text-white border-purple-500/30 ml-auto'
                    : 'bg-white/10 text-gray-100 border-white/20 max-w-xs sm:max-w-sm lg:max-w-md'
                } ${message.user_id === user?.id ? 'rounded-br-md' : 'rounded-tl-md'}`}
              >
                <div className="break-words leading-relaxed">{message.content}</div>
                <div className={`text-xs mt-2 opacity-70 ${
                  message.user_id === user?.id ? 'text-purple-100' : 'text-gray-400'
                }`}>
                  {formatTime(message.created_at)}
                </div>
              </div>
              
              {/* Own Avatar - positioned at top right for own messages */}
              {message.user_id === user?.id && (
                <div className="flex justify-end mb-1">
                  <div className="w-8 h-8 rounded-full overflow-hidden border border-green-500/30 flex-shrink-0">
                    {user?.avatar_url ? (
                      user.avatar_url.startsWith('data:') ? (
                        <img 
                          src={user.avatar_url} 
                          alt="Avatar" 
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        <div 
                          className="w-full h-full"
                          dangerouslySetInnerHTML={{ 
                            __html: defaultAvatars.find(a => a.id === user.avatar_url)?.svg || '' 
                          }}
                        />
                      )
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center">
                        <span className="text-white text-xs font-semibold">
                          {user?.nickname?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
        
        {/* Scroll to Bottom Button - Subtle like Messenger, positioned above input */}
        {showScrollToBottom && (
          <div className="absolute bottom-20 sm:bottom-24 left-1/2 transform -translate-x-1/2 z-10">
            <button
              onClick={() => scrollToBottom(true)}
              className="bg-gray-700/60 backdrop-blur-md hover:bg-gray-600/70 text-white/80 hover:text-white p-2.5 rounded-full shadow-lg border border-white/20 transition-all duration-300 opacity-70 hover:opacity-100 hover:scale-105"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7" />
              </svg>
            </button>
          </div>
        )}
        
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Începe conversația</h3>
            <p className="text-gray-400 max-w-sm">
              Fii primul care trimite un mesaj în această cameră. Începe o conversație interesantă!
            </p>
          </div>
        )}
      </div>

      {/* Messenger-style Input pentru Room Chat */}
      <div className="p-2 sm:p-6 border-t border-white/10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
          <MessengerInput
            value={newMessage}
            onChange={setNewMessage}
            onSubmit={() => {
              if (newMessage.trim()) {
                // Create a mock event object for sendMessage
                const mockEvent = { preventDefault: () => {} };
                sendMessage(mockEvent);
              }
            }}
            placeholder={`Trimite un mesaj în ${room.name}...`}
            maxLength={1000}
          />
        </div>
      </div>

      {/* Friends List Panel - Responsive */}
      {showUserList && (
        <div className="absolute top-16 right-2 sm:right-6 w-full max-w-sm sm:max-w-md md:w-96 bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl z-50 max-h-[70vh] sm:max-h-[500px] overflow-hidden mx-2 sm:mx-0">
          <div className="p-4 border-b border-white/10 bg-gradient-to-r from-purple-600/20 to-blue-600/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                <h3 className="text-white font-semibold text-lg">Prieteni activi</h3>
              </div>
              <button
                onClick={() => setShowUserList(false)}
                className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-white/10 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="overflow-y-auto max-h-80">
            {roomUsers.length === 0 ? (
              <div className="p-6 text-center text-gray-400">
                <div className="text-4xl mb-2">👥</div>
                <p>Nu ai prieteni activi în această cameră</p>
                <p className="text-sm mt-1">Adaugă utilizatori la favorite pentru a-i vedea aici</p>
              </div>
            ) : (
              roomUsers.map((roomUser) => (
                <div key={roomUser.id} className="border-b border-white/5 last:border-b-0 hover:bg-white/5 transition-colors">
                  <div className="p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full overflow-hidden border border-purple-500/30">
                          {roomUser.avatar_url ? (
                            roomUser.avatar_url.startsWith('data:') ? (
                              <img 
                                src={roomUser.avatar_url} 
                                alt="Avatar" 
                                className="w-full h-full object-cover" 
                              />
                            ) : (
                              <div 
                                className="w-full h-full"
                                dangerouslySetInnerHTML={{ 
                                  __html: defaultAvatars.find(a => a.id === roomUser.avatar_url)?.svg || '' 
                                }}
                              />
                            )
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                              <span className="text-white text-sm font-semibold">
                                {roomUser.nickname?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        {roomUser.unread_count > 0 && (
                          <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full border-2 border-gray-800 flex items-center justify-center">
                            <span className="text-white text-xs font-bold">
                              {roomUser.unread_count > 99 ? '99+' : roomUser.unread_count}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-semibold">{roomUser.nickname}</h3>
                        <p className="text-gray-400 text-sm">
                          {roomUser.is_friend ? '★ Favorit' : 'Utilizator în cameră'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Action Buttons Row */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openPrivateChat({
                          id: roomUser.id,
                          nickname: roomUser.nickname,
                          avatar_url: roomUser.avatar_url
                        })}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm transition-colors duration-200 flex items-center justify-center space-x-2"
                        title="Chat privat"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <span>Chat</span>
                      </button>
                      <button
                        onClick={() => removeFromFavorites(roomUser.id)}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm transition-colors duration-200 flex items-center justify-center space-x-2"
                        title="Elimină din favorit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                        <span>Unfavorite</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Private Chat Panel */}
      {showPrivateChat && privateChatUser && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800/95 backdrop-blur-xl rounded-xl border border-white/20 shadow-2xl w-full max-w-2xl h-96 flex flex-col">
            {/* Private Chat Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full overflow-hidden border border-purple-500/30">
                  {privateChatUser.avatar_url ? (
                    privateChatUser.avatar_url.startsWith('data:') ? (
                      <img 
                        src={privateChatUser.avatar_url} 
                        alt="Avatar" 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <div 
                        className="w-full h-full"
                        dangerouslySetInnerHTML={{ 
                          __html: defaultAvatars.find(a => a.id === privateChatUser.avatar_url)?.svg || '' 
                        }}
                      />
                    )
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">
                        {privateChatUser.nickname?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold">Chat privat cu {privateChatUser.nickname}</h3>
                  <p className="text-gray-400 text-sm flex items-center">
                    Conversație privată
                    {isPrivateChatUserFriend && (
                      <span className="ml-2 text-green-400 text-xs">★ Favorit</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleFriendStatus}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${
                    isPrivateChatUserFriend 
                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                      : 'bg-purple-600 hover:bg-purple-700 text-white'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isPrivateChatUserFriend ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    )}
                  </svg>
                  <span>{isPrivateChatUserFriend ? 'FAVORIT' : 'FAVORIT'}</span>
                </button>
                <button
                  onClick={() => {
                    setShowPrivateChat(false);
                    setPrivateChatUser(null);
                    setPrivateMessages([]);
                    setIsPrivateChatUserFriend(false);
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Private Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {privateMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                    msg.sender_id === user?.id
                      ? 'bg-gradient-to-br from-purple-600 to-blue-600 text-white'
                      : 'bg-white/10 text-gray-100'
                  }`}>
                    <div className="break-words">{msg.content}</div>
                    <div className={`text-xs mt-1 opacity-70 ${
                      msg.sender_id === user?.id ? 'text-purple-100' : 'text-gray-400'
                    }`}>
                      {formatTime(msg.created_at)}
                    </div>
                  </div>
                </div>
              ))}
              {privateMessages.length === 0 && (
                <div className="text-center text-gray-400">
                  <p>Încă nu aveți mesaje. Începeți conversația!</p>
                </div>
              )}
            </div>

            {/* Messenger-style Input pentru Private Chat */}
            <div className="p-4 border-t border-white/10">
              <MessengerInput
                value={newPrivateMessage}
                onChange={setNewPrivateMessage}
                onSubmit={() => {
                  if (newPrivateMessage.trim()) {
                    const mockEvent = { preventDefault: () => {} };
                    sendPrivateMessage(mockEvent);
                  }
                }}
                placeholder={`Mesaj pentru ${privateChatUser.nickname}...`}
                maxLength={1000}
              />
            </div>
          </div>
        </div>
      )}
      {/* No longer need avatar bubble popup - actions are inline now */}
    </div>
  );
};

// Helper function to get room icon based on room name/type
const getRoomIcon = (roomName, isPrivate) => {
  const name = roomName.toLowerCase();
  
  if (isPrivate) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center shadow-lg">
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }
  
  if (name.includes('general') || name.includes('main') || name.includes('lobby')) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }
  
  if (name.includes('gaming') || name.includes('game') || name.includes('play')) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0zM14 4a2 2 0 100 4 2 2 0 000-4zM6 4a2 2 0 100 4 2 2 0 000-4zM10 15a4 4 0 01-4-4v-1h8v1a4 4 0 01-4 4z"/>
        </svg>
      </div>
    );
  }
  
  if (name.includes('music') || name.includes('audio') || name.includes('sound')) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-lg">
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z"/>
        </svg>
      </div>
    );
  }
  
  if (name.includes('tech') || name.includes('dev') || name.includes('code') || name.includes('programming')) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center shadow-lg">
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }
  
  if (name.includes('movie') || name.includes('film') || name.includes('cinema') || name.includes('tv')) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center shadow-lg">
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"/>
        </svg>
      </div>
    );
  }
  
  if (name.includes('news') || name.includes('announce') || name.includes('info')) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg">
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }
  
  if (name.includes('help') || name.includes('support') || name.includes('question')) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center shadow-lg">
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }
  
  if (name.includes('random') || name.includes('misc') || name.includes('other')) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow-lg">
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
        </svg>
      </div>
    );
  }
  
  // Default icon for any other room
  return (
    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
      </svg>
    </div>
  );
};

const RoomList = ({ 
  onRoomSelect, 
  onAccountSettings,
  showPostModal,
  setShowPostModal,
  newPost,
  setNewPost,
  handleImageUpload,
  createPost,
  uploadedImages,
  removeImage,
  linkPreview,
  isLoadingPreview,
  notifications,
  unreadNotificationsCount,
  showNotifications,
  setShowNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  following,
  followers,
  suggestedUsers,
  trendingHashtags,
  postsInteractions,
  onLike,
  onRepost,
  onQuote,
  onReply,
  onShare,
  onFollow,
  onViewProfile,
  setShowSearchPage,
  setShowDirectMessages,
  timelineMode,
  setTimelineMode,
  posts,
  setPosts,
  worldChatTypingRef
}) => {
  const [rooms, setRooms] = useState([]);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomDescription, setNewRoomDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  
  // Floating button și modal states
  const [floatingButtonOpacity, setFloatingButtonOpacity] = useState(1); // 1 = opac, 0.4 = mai transparent
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Avatar dropdown state
  const [showAvatarDropdown, setShowAvatarDropdown] = useState(false);
  
  const { token, user, logout } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    fetchRooms();
  }, []);

  // Scroll detection pentru floating button - Transparență în loc de ascundere
  useEffect(() => {
    const handleScroll = () => {
      const feedElement = document.getElementById('world-chat-feed');
      if (!feedElement) return;

      const currentScrollY = feedElement.scrollTop;
      const isScrollingUp = currentScrollY < lastScrollY;
      const isScrolledDown = currentScrollY > 100; // Doar după ce s-a scrollat în jos

      // Opac când scroll-ezi în sus, mai transparent (40%) când scroll-ezi în jos
      if (isScrolledDown) {
        setFloatingButtonOpacity(isScrollingUp ? 1 : 0.4);
      } else {
        setFloatingButtonOpacity(1); // Mereu opac când ești la top
      }
      
      setLastScrollY(currentScrollY);
    };

    const feedElement = document.getElementById('world-chat-feed');
    if (feedElement) {
      feedElement.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (feedElement) {
        feedElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [lastScrollY]);

  // Close avatar dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAvatarDropdown && !event.target.closest('.avatar-dropdown-container')) {
        setShowAvatarDropdown(false);
      }
    };

    if (showAvatarDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showAvatarDropdown]);

  const fetchRooms = async () => {
    try {
      const response = await api.get(`${API}/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(response.data);
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    }
  };

  const createRoom = async (e) => {
    e.preventDefault();
    if (!newRoomName.trim()) return;

    try {
      await api.post(`${API}/rooms`, {
        name: newRoomName.trim(),
        description: newRoomDescription.trim() || null,
        is_private: isPrivate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewRoomName('');
      setNewRoomDescription('');
      setIsPrivate(false);
      setShowCreateRoom(false);
      fetchRooms();
    } catch (error) {
      console.error('Failed to create room:', error);
    }
  };

  const joinRoom = async (roomId) => {
    try {
      await api.post(`${API}/rooms/${roomId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRooms();
    } catch (error) {
      console.error('Failed to join room:', error);
    }
  };

  // State pentru noua interfață cu taburi
  const [activeTab, setActiveTab] = useState('world-chat');
  // posts and setPosts are now received as props from AppContent
  const [newPostLink, setNewPostLink] = useState('');
  
  // New state for Facebook-style post creation
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [selectedImageModal, setSelectedImageModal] = useState(null);
  const [characterCount, setCharacterCount] = useState(0);
  const MAX_CHARACTERS = 5000;
  
  // New state for sharing functionality
  const [shareModalPost, setShareModalPost] = useState(null);
  const [shareOption, setShareOption] = useState(''); // 'room' or 'friend'
  const [shareComment, setShareComment] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [selectedFriend, setSelectedFriend] = useState('');
  const [userRooms, setUserRooms] = useState([]);
  const [userFavorites, setUserFavorites] = useState([]);

  // New state for Camera ta functionality
  const [userCameras, setUserCameras] = useState([]);
  const [showCreateCamera, setShowCreateCamera] = useState(false);
  const [newCamera, setNewCamera] = useState({
    name: '',
    theme: '',
    description: '',
    rules: '',
    visibility: 'public', // public, private, invite-only
    allowInvites: true,
    maxMembers: 100
  });
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [cameraMembers, setCameraMembers] = useState([]);
  const [showCameraSettings, setShowCameraSettings] = useState(false);

  // New state for Favorites functionality
  const [favoriteUsers, setFavoriteUsers] = useState([]);
  const [selectedFavorite, setSelectedFavorite] = useState(null);
  const [showFavoriteActions, setShowFavoriteActions] = useState(null);

  const loadPosts = async (mode = timelineMode) => {
    try {
      // Use different endpoint based on timeline mode
      const endpoint = mode === 'for-you' 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/world-chat/posts/for-you`
        : `${process.env.REACT_APP_BACKEND_URL}/api/world-chat/posts`;
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const postsData = await response.json();
        setPosts(postsData);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  // Load posts when World Chat tab becomes active or timeline mode changes
  useEffect(() => {
    if (activeTab === 'world-chat') {
      loadPosts(timelineMode);
    }
  }, [activeTab, timelineMode]);
  
  const formatPostDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return diffMins < 1 ? 'acum' : `${diffMins}m`;
    } else if (diffHours < 24) {
      return `${diffHours}h`;
    } else if (diffDays < 7) {
      return `${diffDays}d`;
    } else {
      return date.toLocaleDateString('ro-RO');
    }
  };

  // Sharing functions
  const openShareModal = (post) => {
    setShareModalPost(post);
    setShareOption('');
    setShareComment('');
    setSelectedRoom('');
    setSelectedFriend('');
    // Load user rooms and favorites when opening modal
    loadUserRooms();
    loadUserFavorites();
  };

  const closeShareModal = () => {
    setShareModalPost(null);
    setShareOption('');
    setShareComment('');
    setSelectedRoom('');
    setSelectedFriend('');
  };

  const loadUserRooms = async () => {
    // Mock data for now - in real implementation, fetch from API
    setUserRooms([
      { id: '1', name: 'Camera mea tehnologie', type: 'public' },
      { id: '2', name: 'Discusii private', type: 'private' },
      { id: '3', name: 'Hobby & Gaming', type: 'public' }
    ]);
  };

  const loadUserFavorites = async () => {
    // Mock data for now - in real implementation, fetch from favorites API
    setUserFavorites([
      { id: '1', name: 'Ana Popescu', nickname: 'ana_pop', avatar: null },
      { id: '2', name: 'Mihai Ion', nickname: 'mihai_i', avatar: null },
      { id: '3', name: 'Elena Marin', nickname: 'elena_m', avatar: null }
    ]);
  };

  const handleShare = async () => {
    if (!shareModalPost) return;

    try {
      const shareData = {
        post_id: shareModalPost.id,
        comment: shareComment.trim(),
        share_type: shareOption,
        target_id: shareOption === 'room' ? selectedRoom : selectedFriend
      };

      // For now, just show success message - in real implementation, call API
      if (shareOption === 'room') {
        const roomName = userRooms.find(r => r.id === selectedRoom)?.name;
        alert(`Postarea a fost partajată în camera "${roomName}"!`);
      } else if (shareOption === 'friend') {
        const friendName = userFavorites.find(f => f.id === selectedFriend)?.name;
        alert(`Postarea a fost partajată către "${friendName}"!`);
      }

      closeShareModal();
    } catch (error) {
      console.error('Error sharing post:', error);
      alert('Eroare la partajarea postării');
    }
  };

  // Load user's rooms from API
  const loadMyRooms = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rooms/mine`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const rooms = await response.json();
        // Transform API response to match frontend structure
        const transformedRooms = rooms.map(room => ({
          id: room.id,
          name: room.name,
          description: room.description || '',
          visibility: room.is_private ? 'private' : 'public',
          membersCount: room.member_count,
          messagesCount: room.messages_count,
          createdAt: new Date(room.created_at),
          isActive: room.is_active,
          theme: 'General'
        }));
        setUserCameras(transformedRooms);
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
    }
  };

  // Load rooms when my-room tab becomes active
  useEffect(() => {
    if (activeTab === 'my-room') {
      loadMyRooms();
    }
  }, [activeTab]);

  const handleCreateCamera = async () => {
    if (!newCamera.name.trim()) {
      alert('Numele camerei este obligatoriu');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rooms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newCamera.name.trim(),
          description: newCamera.description || '',
          is_private: newCamera.visibility === 'private'
        })
      });
      
      if (response.ok) {
        // Reload rooms
        await loadMyRooms();
        
        // Reset form
        setNewCamera({
          name: '',
          theme: '',
          description: '',
          rules: '',
          visibility: 'public',
          allowInvites: true,
          maxMembers: 100
        });
        
        setShowCreateCamera(false);
        alert(`Camera "${newCamera.name}" a fost creată cu succes!`);
      } else {
        const error = await response.json();
        alert(`Eroare: ${error.detail || 'Nu s-a putut crea camera'}`);
      }
    } catch (error) {
      console.error('Error creating camera:', error);
      alert('Eroare la crearea camerei');
    }
  };

  const handleCameraAction = async (action, camera) => {
    switch (action) {
      case 'enter':
        setSelectedCamera(camera);
        break;
      case 'settings':
        setSelectedCamera(camera);
        setShowCameraSettings(true);
        break;
      case 'delete':
        if (window.confirm(`Ești sigur că vrei să ștergi camera "${camera.name}"?`)) {
          try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rooms/${camera.id}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
            
            if (response.ok) {
              setUserCameras(prev => prev.filter(c => c.id !== camera.id));
              alert('Camera a fost ștearsă cu succes');
            } else {
              const error = await response.json();
              alert(`Eroare: ${error.detail || 'Nu s-a putut șterge camera'}`);
            }
          } catch (error) {
            console.error('Error deleting room:', error);
            alert('Eroare la ștergerea camerei');
          }
        }
        break;
      case 'toggle':
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rooms/${camera.id}/toggle-active`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const result = await response.json();
            setUserCameras(prev => prev.map(c => 
              c.id === camera.id ? { ...c, isActive: result.is_active } : c
            ));
          }
        } catch (error) {
          console.error('Error toggling room:', error);
        }
        break;
      default:
        break;
    }
  };

  const getCameraVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'public': return '🌍';
      case 'private': return '🔒';
      case 'invite-only': return '📧';
      default: return '🌍';
    }
  };

  const getCameraVisibilityText = (visibility) => {
    switch (visibility) {
      case 'public': return 'Publică';
      case 'private': return 'Privată';
      case 'invite-only': return 'Doar cu invitație';
      default: return 'Publică';
    }
  };

  // Camera Card Component
  const CameraCard = ({ camera, onAction }) => {
    const getCategoryBadge = () => {
      if (camera.isOwned) return { text: 'A mea', color: 'bg-green-500/20 text-green-400 border-green-500/30' };
      if (camera.isFollowing) return { text: 'Urmăresc', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
      if (camera.hasInteracted) return { text: 'Vizitată', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
      if (camera.category === 'suggested') return { text: 'Sugerată', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' };
      return null;
    };

    const badge = getCategoryBadge();

    return (
      <div className={`bg-gray-800/50 rounded-xl border border-white/10 overflow-hidden hover:border-cyan-400/30 transition-all duration-200 ${!camera.isActive ? 'opacity-60' : ''}`}>
        {/* Camera Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-lg">{getCameraVisibilityIcon(camera.visibility)}</span>
                <h3 className="text-white font-semibold text-lg line-clamp-1">{camera.name}</h3>
                {badge && (
                  <span className={`text-xs px-2 py-1 rounded-full border ${badge.color}`}>
                    {badge.text}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-3 text-xs text-gray-400">
                <span>{getCameraVisibilityText(camera.visibility)}</span>
                <span>•</span>
                <span>{camera.theme}</span>
                {camera.owner && !camera.isOwned && (
                  <>
                    <span>•</span>
                    <span>de {camera.owner}</span>
                  </>
                )}
              </div>
            </div>
            <div className="relative ml-2">
              <button className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-700/50 transition-colors">
                <span className="text-sm">⋯</span>
              </button>
            </div>
          </div>
        </div>

        {/* Camera Content */}
        <div className="p-4 space-y-3">
          {camera.description && (
            <p className="text-gray-300 text-sm line-clamp-2">{camera.description}</p>
          )}
          
          {camera.pinnedMessage && (
            <div className="bg-cyan-400/10 border border-cyan-400/20 rounded-lg p-2">
              <div className="text-xs text-cyan-400 mb-1">📌 Mesaj fixat</div>
              <div className="text-white text-sm line-clamp-2">{camera.pinnedMessage}</div>
            </div>
          )}

          {camera.suggestionReason && (
            <div className="bg-purple-400/10 border border-purple-400/20 rounded-lg p-2">
              <div className="text-xs text-purple-400 mb-1">💡 Sugerată</div>
              <div className="text-white text-sm">{camera.suggestionReason}</div>
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center justify-between text-xs text-gray-400">
            <div className="flex items-center space-x-4">
              <span>👥 {camera.membersCount} membri</span>
              <span>💬 {camera.messagesCount} mesaje</span>
            </div>
            <span>{camera.createdAt.toLocaleDateString('ro-RO')}</span>
          </div>
        </div>

        {/* Camera Actions */}
        <div className="p-4 border-t border-white/10 flex items-center space-x-2">
          {camera.isOwned ? (
            // Actions for my rooms
            <>
              <button
                onClick={() => onAction('enter', camera)}
                disabled={!camera.isActive}
                className="flex-1 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-200 text-sm"
              >
                {camera.isActive ? 'Intră' : 'Inactivă'}
              </button>
              <button
                onClick={() => onAction('settings', camera)}
                className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
              >
                ⚙️
              </button>
              <button
                onClick={() => onAction('toggle', camera)}
                className={`px-3 py-2 rounded-lg transition-colors text-sm ${camera.isActive ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
              >
                {camera.isActive ? '⏸️' : '▶️'}
              </button>
              <button
                onClick={() => onAction('delete', camera)}
                className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
              >
                🗑️
              </button>
            </>
          ) : (
            // Actions for other rooms
            <>
              <button
                onClick={() => onAction('enter', camera)}
                className="flex-1 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-medium rounded-lg transition-all duration-200 text-sm"
              >
                Intră
              </button>
              {camera.isFollowing ? (
                <button
                  onClick={() => onAction('unfollow', camera)}
                  className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
                >
                  Nu mai urmări
                </button>
              ) : (
                <button
                  onClick={() => onAction('follow', camera)}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                >
                  Urmărește
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Favorites functions
  useEffect(() => {
    if (activeTab === 'favorites') {
      loadFavoriteUsers();
    }
  }, [activeTab]);

  const loadFavoriteUsers = async () => {
    // Mock data for favorites - in real implementation, fetch from API
    setFavoriteUsers([
      {
        id: '1',
        name: 'Ana Popescu',
        nickname: 'ana_pop',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616c9ca7908?w=100&h=100&fit=crop&crop=face',
        isOnline: true,
        lastSeen: new Date(),
        unreadMessages: 3,
        lastMessage: 'Ai văzut știrea de astăzi?',
        lastMessageTime: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
        isMuted: false,
        commonRooms: ['Presa Liberă', 'Tech Talks România']
      },
      {
        id: '2',
        name: 'Mihai Ionescu',
        nickname: 'mihai_dev',
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
        isOnline: true,
        lastSeen: new Date(),
        unreadMessages: 0,
        lastMessage: 'Perfect! Mulțumesc pentru ajutor.',
        lastMessageTime: new Date(Date.now() - 30 * 60 * 1000), // 30 min ago
        isMuted: false,
        commonRooms: ['Tech Talks România']
      },
      {
        id: '3',
        name: 'Elena Marin',
        nickname: 'elena_m',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
        isOnline: false,
        lastSeen: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
        unreadMessages: 1,
        lastMessage: 'Ne vedem mâine!',
        lastMessageTime: new Date(Date.now() - 4 * 60 * 60 * 1000),
        isMuted: false,
        commonRooms: ['Presa Liberă']
      },
      {
        id: '4',
        name: 'Alexandru Popa',
        nickname: 'alex_crypto',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
        isOnline: false,
        lastSeen: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
        unreadMessages: 5,
        lastMessage: 'Bitcoin a crescut din nou! 🚀',
        lastMessageTime: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
        isMuted: true, // Muted user
        commonRooms: []
      },
      {
        id: '5',
        name: 'Ioana Georgescu',
        nickname: 'ioana_travel',
        avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face',
        isOnline: true,
        lastSeen: new Date(),
        unreadMessages: 0,
        lastMessage: 'Brasovul este superb toamna!',
        lastMessageTime: new Date(Date.now() - 12 * 60 * 60 * 1000), // 12 hours ago
        isMuted: false,
        commonRooms: []
      },
      {
        id: '6',
        name: 'Dan Radu',
        nickname: 'dan_business',
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
        isOnline: false,
        lastSeen: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
        unreadMessages: 0,
        lastMessage: 'Să discutăm despre proiect.',
        lastMessageTime: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        isMuted: false,
        commonRooms: []
      }
    ]);
  };

  const handleFavoriteAction = (action, user) => {
    switch (action) {
      case 'dm':
        // Open direct message
        alert(`Opening DM with ${user.name}`);
        break;
      case 'jump':
        // Jump to last message
        alert(`Jumping to last message with ${user.name}`);
        break;
      case 'mute':
        // Toggle mute
        setFavoriteUsers(prev => prev.map(u => 
          u.id === user.id ? { ...u, isMuted: !u.isMuted } : u
        ));
        break;
      case 'unfollow':
        // Remove from favorites
        if (window.confirm(`Ești sigur că vrei să elimini pe ${user.name} din favoriți?`)) {
          setFavoriteUsers(prev => prev.filter(u => u.id !== user.id));
        }
        break;
      default:
        break;
    }
    setShowFavoriteActions(null);
  };

  const formatLastSeen = (lastSeen) => {
    const now = new Date();
    const diffMs = now - lastSeen;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return diffMins < 1 ? 'acum' : `${diffMins}min`;
    } else if (diffHours < 24) {
      return `${diffHours}h`;
    } else if (diffDays < 7) {
      return `${diffDays}d`;
    } else {
      return lastSeen.toLocaleDateString('ro-RO');
    }
  };

  return (
    <div className="h-screen bg-gray-900 flex flex-col android-viewport-fix">
      {/* Header optimizat pentru Android și iOS */}
      <div className="bg-gradient-to-r from-gray-800/95 to-gray-900/95 backdrop-blur-xl mobile-header border-b border-white/10 shadow-lg ios-safe-area-top">
        <div className="flex items-center justify-between w-full">
          {/* Logo VONEX - Responsive - Mărit cu 20% */}
          <div className="flex-shrink-0">
            <img 
              src="/vonex-original-logo.png" 
              alt="VONEX" 
              className="h-7 sm:h-10 w-auto object-contain retina-optimized mobile-logo-small"
              style={{
                filter: 'drop-shadow(0 0 6px rgba(0, 255, 255, 0.3)) drop-shadow(0 0 10px rgba(68, 255, 68, 0.2))'
              }}
            />
          </div>

          {/* Taburi responsive - horizontal scroll pe mobile */}
          <div className="flex-1 mx-2 overflow-x-auto scrollbar-hide">
            <div className="flex mobile-tabs">
              {[
                { id: 'world-chat', name: 'World Chat' },
                { id: 'my-room', name: 'Camerele mele' },
                { id: 'following', name: 'Following' }, // Changed from 'favorites' 
                { id: 'recent', name: 'Recente' },
                { id: 'search', name: 'Search' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.id === 'search') {
                      setShowSearchPage(true);
                    } else {
                      setActiveTab(tab.id);
                    }
                  }}
                  className={`mobile-tab mobile-transition touch-target mobile-focus android-ripple ios-button relative ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 text-white border border-cyan-400/50 shadow-lg scale-105'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700/30 border border-transparent hover:border-gray-600/30'
                  }`}
                  aria-label={`${tab.name} tab`}
                  data-testid={`nav-tab-${tab.id}`}
                >
                  <span className="font-medium">{tab.name}</span>
                  {/* Badge pentru unread messages - optimizat mobile */}
                  {tab.id === 'favorites' && favoriteUsers.reduce((total, user) => total + user.unreadMessages, 0) > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                      {favoriteUsers.reduce((total, user) => total + user.unreadMessages, 0) > 9 
                        ? '9+' 
                        : favoriteUsers.reduce((total, user) => total + user.unreadMessages, 0)
                      }
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Notifications Badge */}
          <div className="flex-shrink-0 mr-3">
            <NotificationBadge 
              count={unreadNotificationsCount}
              onClick={() => setShowNotifications(true)}
            />
          </div>
          <div className="flex-shrink-0 relative avatar-dropdown-container">
            <button
              onClick={() => setShowAvatarDropdown(!showAvatarDropdown)}
              className="touch-target p-1 rounded-full hover:bg-gray-700/30 mobile-transition mobile-focus android-ripple relative"
              title="User Menu"
              aria-label="Deschide meniul utilizatorului"
            >
              <div className="relative">
                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-500 hover:border-purple-400 mobile-transition">
                  {user?.avatar_url ? (
                    user.avatar_url.startsWith('data:') ? (
                      <img 
                        src={user.avatar_url} 
                        alt="Avatar" 
                        className="w-full h-full object-cover retina-optimized" 
                      />
                    ) : (
                      <div 
                        className="w-full h-full"
                        dangerouslySetInnerHTML={{ 
                          __html: defaultAvatars.find(a => a.id === user.avatar_url)?.svg || '' 
                        }}
                      />
                    )
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                      <span className="text-white text-base font-bold">
                        {user?.nickname?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}
                </div>
                {/* Online status indicator - mărit proporțional */}
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-gray-800"></div>
              </div>
            </button>

            {/* Avatar Dropdown */}
            <AvatarDropdown
              user={user}
              onAccountSettings={() => onAccountSettings()}
              onLogout={logout}
              isOpen={showAvatarDropdown}
              onClose={() => setShowAvatarDropdown(false)}
            />
          </div>
        </div>
      </div>

      {/* Content Area - Clean */}
      <div className="flex-1 overflow-hidden mobile-scroll ios-no-bounce">
        {activeTab === 'world-chat' && (
          <div className="h-full flex flex-col">
            {/* World Chat Header with Timeline Tabs */}
            <div className="bg-gray-800/50 border-b border-white/10">
              {/* Top row with title and DM button */}
              <div className="px-6 py-3 flex items-center justify-between">
                <h1 className="text-xl font-bold text-white">Acasă</h1>
                <button
                  onClick={() => setShowDirectMessages && setShowDirectMessages(true)}
                  className="p-2 hover:bg-gray-700/50 rounded-full transition-colors"
                  data-testid="dm-button"
                  title="Mesaje directe"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              
              {/* Timeline Tabs */}
              <div className="flex border-t border-white/5">
                <button
                  onClick={() => setTimelineMode && setTimelineMode('for-you')}
                  className={`flex-1 py-3 text-center font-semibold transition-colors relative ${
                    timelineMode === 'for-you' 
                      ? 'text-white' 
                      : 'text-gray-500 hover:bg-gray-700/30'
                  }`}
                  data-testid="for-you-tab"
                >
                  Pentru tine
                  {timelineMode === 'for-you' && (
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-1 bg-cyan-500 rounded-full"></div>
                  )}
                </button>
                <button
                  onClick={() => setTimelineMode && setTimelineMode('chronological')}
                  className={`flex-1 py-3 text-center font-semibold transition-colors relative ${
                    timelineMode === 'chronological' 
                      ? 'text-white' 
                      : 'text-gray-500 hover:bg-gray-700/30'
                  }`}
                  data-testid="following-tab"
                >
                  Urmăresc
                  {timelineMode === 'chronological' && (
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-1 bg-cyan-500 rounded-full"></div>
                  )}
                </button>
              </div>
            </div>

            {/* World Chat Typing Indicator */}
            <WorldChatTypingIndicator ref={worldChatTypingRef} token={token} />

            {/* Post Feed */}
            <div className="flex-1 overflow-y-auto" id="world-chat-feed">
              <div className="max-w-full md:max-w-2xl md:mx-auto p-4 md:p-6">
                {posts.length > 0 ? (
                  <div className="space-y-6">
                    {posts.map((post) => (
                      <div key={post.id} className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-white/10 shadow-lg">
                        {/* Post Header - cu avatar clickable */}
                        <div className="p-4 flex items-start space-x-3">
                          {/* Avatar clickable */}
                          <button
                            onClick={() => onViewProfile && onViewProfile(post.user_id)}
                            className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 overflow-hidden hover:ring-2 hover:ring-cyan-500 transition-all"
                            data-testid={`post-avatar-${post.id}`}
                          >
                            {post.user_avatar ? (
                              <img 
                                src={post.user_avatar} 
                                alt={post.user_name} 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white font-bold">
                                {(post.user_name?.[0] || post.user_nickname?.[0] || 'U').toUpperCase()}
                              </div>
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => onViewProfile && onViewProfile(post.user_id)}
                                className="text-white font-semibold text-sm hover:underline truncate"
                              >
                                {post.user_name || post.user_nickname}
                              </button>
                              <span className="text-gray-500 text-sm truncate">@{post.user_nickname}</span>
                              <span className="text-gray-600">·</span>
                              <span className="text-gray-400 text-xs">{formatPostDate(post.created_at)}</span>
                            </div>
                          </div>
                          <div className="relative">
                            <button className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-700/50 transition-colors">
                              <span className="text-lg">⋯</span>
                            </button>
                          </div>
                        </div>

                        {/* Post Content */}
                        {post.content && (
                          <div className="px-4 pb-3">
                            <div className="text-white whitespace-pre-wrap break-words text-sm leading-relaxed">
                              {post.content.length > 500 ? (
                                <>
                                  {post.content.substring(0, 500)}...
                                  <button className="text-cyan-400 hover:text-cyan-300 ml-1 text-sm font-medium">
                                    Vezi mai mult
                                  </button>
                                </>
                              ) : (
                                post.content
                              )}
                            </div>
                          </div>
                        )}

                        {/* Post Images */}
                        {post.images && post.images.length > 0 && (
                          <div className="pb-3">
                            {post.images.length === 1 ? (
                              <div className="px-0 md:px-4">
                                <img 
                                  src={`${process.env.REACT_APP_BACKEND_URL}${post.images[0].thumbnail_url}`}
                                  alt="Post image"
                                  className="w-full max-h-96 object-cover md:rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => setSelectedImageModal(post.images[0])}
                                />
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 gap-1 px-0 md:px-4">
                                {post.images.slice(0, 4).map((image, index) => (
                                  <div key={image.id} className="relative">
                                    <img 
                                      src={`${process.env.REACT_APP_BACKEND_URL}${image.thumbnail_url}`}
                                      alt={`Post image ${index + 1}`}
                                      className="w-full h-48 object-cover md:rounded cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={() => setSelectedImageModal(image)}
                                    />
                                    {index === 3 && post.images.length > 4 && (
                                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-bold text-lg md:rounded cursor-pointer">
                                        +{post.images.length - 4}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Link Preview */}
                        {post.link_preview && (
                          <div className="px-4 pb-3">
                            <div className="bg-gray-700/30 border border-white/10 rounded-lg overflow-hidden hover:bg-gray-700/40 transition-colors cursor-pointer">
                              {post.link_preview.image_url && (
                                <img 
                                  src={post.link_preview.image_url} 
                                  alt="Link preview"
                                  className="w-full h-48 object-cover"
                                />
                              )}
                              <div className="p-4 space-y-2">
                                <div className="text-xs text-gray-400 uppercase font-medium">{post.link_preview.domain}</div>
                                {post.link_preview.title && (
                                  <div className="text-white font-medium text-sm line-clamp-2">{post.link_preview.title}</div>
                                )}
                                {post.link_preview.description && (
                                  <div className="text-gray-300 text-sm line-clamp-2">{post.link_preview.description}</div>
                                )}
                                <a 
                                  href={post.link_preview.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-cyan-400 hover:text-cyan-300 text-sm inline-flex items-center space-x-1 font-medium"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <span>Vizitează</span>
                                  <span>↗</span>
                                </a>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Quoted Post Embed (for quote posts) */}
                        {post.quoted_post && (
                          <div className="px-4 pb-3">
                            <QuotedPostEmbed 
                              post={post.quoted_post}
                              onClick={() => {
                                // Could navigate to original post or expand it
                                console.log('Clicked on quoted post:', post.quoted_post.id);
                              }}
                            />
                          </div>
                        )}

                        {/* Post Interactions */}
                        <div className="px-4 pb-3 pt-2 border-t border-white/5">
                          <PostInteractions
                            postId={post.id}
                            interactions={postsInteractions[post.id] || { likes: 0, reposts: 0, comments_count: 0, isLiked: false, isReposted: false }}
                            onLike={onLike}
                            onRepost={onRepost}
                            onQuote={onQuote}
                            onComment={onReply}
                            onShare={onShare}
                          />
                        </div>

                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 px-4">
                    <h3 className="text-xl font-semibold text-white mb-2">Welcome to World Chat!</h3>
                    <p className="text-gray-400 mb-6">Primul feed global VONEX. Împarte știri, link-uri și idei cu toată comunitatea.</p>
                    <p className="text-sm text-gray-500">Fii primul care postează ceva!</p>
                  </div>
                )}
              </div>
            </div>

            {/* Floating Post Button - doar pe mobile */}
            <FloatingPostButton
              opacity={floatingButtonOpacity}
              onCreatePost={() => setShowPostModal(true)}
            />
          </div>
        )}

        {activeTab === 'my-room' && (
          <div className="h-full flex flex-col">
            {!selectedCamera ? (
              /* Camera List View */
              <div className="flex-1 overflow-y-auto">
                {/* Header */}
                <div className="bg-gray-800/50 p-6 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <h1 className="text-xl font-bold text-white">Camerele tale</h1>
                      <p className="text-gray-400 text-sm">Creează și gestionează propriile camere tematice</p>
                    </div>
                    <button
                      onClick={() => setShowCreateCamera(true)}
                      className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-medium rounded-lg transition-all duration-200 flex items-center space-x-2"
                    >
                      <span>➕</span>
                      <span>Cameră nouă</span>
                    </button>
                  </div>
                </div>

                {/* My Cameras Only */}
                <div className="flex-1 p-6">
                  <div className="max-w-full md:max-w-4xl md:mx-auto">
                    {userCameras.length === 0 ? (
                      <div className="text-center py-12">
                        <span className="text-6xl mb-4 block">🏠</span>
                        <h3 className="text-xl font-semibold text-white mb-2">Nicio cameră creată încă</h3>
                        <p className="text-gray-400 mb-6">Creează prima ta cameră tematică și invită prietenii să discute!</p>
                        <button
                          onClick={() => setShowCreateCamera(true)}
                          className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-medium rounded-lg transition-all duration-200"
                        >
                          Creează prima cameră
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {userCameras.map((camera) => (
                          <div key={camera.id} className={`bg-gray-800/50 rounded-xl border border-white/10 overflow-hidden hover:border-cyan-400/30 transition-all duration-200 ${!camera.isActive ? 'opacity-60' : ''}`}>
                            {/* Camera Header */}
                            <div className="p-4 border-b border-white/10">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-lg">{getCameraVisibilityIcon(camera.visibility)}</span>
                                    <h3 className="text-white font-semibold text-lg line-clamp-1">{camera.name}</h3>
                                  </div>
                                  <div className="flex items-center space-x-3 text-xs text-gray-400">
                                    <span>{getCameraVisibilityText(camera.visibility)}</span>
                                    <span>•</span>
                                    <span>{camera.theme}</span>
                                  </div>
                                </div>
                                <div className="relative ml-2">
                                  <button className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-700/50 transition-colors">
                                    <span className="text-sm">⋯</span>
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Camera Content */}
                            <div className="p-4 space-y-3">
                              {camera.description && (
                                <p className="text-gray-300 text-sm line-clamp-2">{camera.description}</p>
                              )}
                              
                              {camera.pinnedMessage && (
                                <div className="bg-cyan-400/10 border border-cyan-400/20 rounded-lg p-2">
                                  <div className="text-xs text-cyan-400 mb-1">📌 Mesaj fixat</div>
                                  <div className="text-white text-sm line-clamp-2">{camera.pinnedMessage}</div>
                                </div>
                              )}

                              {/* Stats */}
                              <div className="flex items-center justify-between text-xs text-gray-400">
                                <div className="flex items-center space-x-4">
                                  <span>👥 {camera.membersCount} membri</span>
                                  <span>💬 {camera.messagesCount} mesaje</span>
                                </div>
                                <span>{camera.createdAt.toLocaleDateString('ro-RO')}</span>
                              </div>
                            </div>

                            {/* Camera Actions - Full control for my rooms */}
                            <div className="p-4 border-t border-white/10 flex items-center space-x-2">
                              <button
                                onClick={() => handleCameraAction('enter', camera)}
                                disabled={!camera.isActive}
                                className="flex-1 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-200 text-sm"
                              >
                                {camera.isActive ? 'Intră' : 'Inactivă'}
                              </button>
                              <button
                                onClick={() => handleCameraAction('settings', camera)}
                                className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                                title="Setări cameră"
                              >
                                ⚙️
                              </button>
                              <button
                                onClick={() => handleCameraAction('toggle', camera)}
                                className={`px-3 py-2 rounded-lg transition-colors text-sm ${camera.isActive ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
                                title={camera.isActive ? 'Pauzează camera' : 'Activează camera'}
                              >
                                {camera.isActive ? '⏸️' : '▶️'}
                              </button>
                              <button
                                onClick={() => handleCameraAction('delete', camera)}
                                className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
                                title="Șterge camera"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Selected Camera View */
              <RoomChat
                room={selectedCamera}
                token={token}
                currentUserId={user?.id}
                onBack={() => setSelectedCamera(null)}
                onSettings={() => setShowCameraSettings(true)}
                onInvite={() => {
                  // TODO: Show invite modal
                  alert('Funcționalitate invitații în dezvoltare');
                }}
              />
            )}
          </div>
        )}

        {activeTab === 'following' && (
          <div className="h-full flex">
            {/* Main Content Area - Left Side */}
            <div className="flex-1 flex">
              {/* Following List */}
              <div className="w-2/3 overflow-y-auto">
                <div className="p-6">
                  <div className="max-w-2xl">
                    <h2 className="text-2xl font-bold text-white mb-6">People you follow</h2>
                    
                    {following.length === 0 ? (
                      <div className="text-center py-12">
                        <span className="text-6xl mb-4 block">👥</span>
                        <h3 className="text-white text-xl mb-2">Follow people to see their posts</h3>
                        <p className="text-gray-400 mb-6">
                          When you follow someone, you'll see their posts in your timeline
                        </p>
                        <button 
                          className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-3 rounded-full transition-colors"
                          onClick={() => setActiveTab('search')}
                        >
                          Find people to follow
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {following.map((followingUser) => (
                          <div key={followingUser.id} className="bg-gray-800/50 rounded-2xl border border-white/10 p-4">
                            <div className="flex items-center justify-between">
                              <button 
                                className="flex items-center space-x-3 flex-1 text-left hover:bg-gray-700/30 p-2 rounded-lg transition-colors"
                                onClick={() => setActiveTab('world-chat')} // TODO: Navigate to user profile
                              >
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0">
                                  <span className="text-white text-lg font-bold">
                                    {followingUser.first_name?.charAt(0)?.toUpperCase() || followingUser.email?.charAt(0)?.toUpperCase() || 'U'}
                                  </span>
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-white font-semibold">
                                    {followingUser.first_name && followingUser.last_name 
                                      ? `${followingUser.first_name} ${followingUser.last_name}`
                                      : followingUser.nickname || followingUser.email || 'Unknown'
                                    }
                                  </h3>
                                  <p className="text-gray-400 text-sm">
                                    @{followingUser.nickname || (followingUser.email ? followingUser.email.split('@')[0] : 'user')}
                                  </p>
                                  <p className="text-gray-500 text-sm mt-1">
                                    {followingUser.bio || 'No bio available'}
                                  </p>
                                </div>
                              </button>
                              
                              <FollowButton
                                userId={followingUser.id}
                                isFollowing={true}
                                onFollow={onFollow}
                                size="normal"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Right Sidebar - Suggestions and Trending */}
              <div className="w-1/3 bg-gray-800/20 border-l border-white/10 p-4 space-y-6 overflow-y-auto">
                {/* Trending Hashtags */}
                <TrendingHashtags
                  hashtags={trendingHashtags}
                  onHashtagClick={(hashtag) => {
                    // TODO: Filter posts by hashtag
                    console.log('Clicked hashtag:', hashtag);
                  }}
                />
                
                {/* User Suggestions */}
                <UserSuggestions
                  users={suggestedUsers}
                  onUserClick={(userId) => {
                    // TODO: Navigate to user profile
                    console.log('Clicked user:', userId);
                  }}
                  onFollow={onFollow}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'recent' && (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-white mb-2">Camere Recente</h3>
              <p className="text-gray-400">În curând...</p>
            </div>
          </div>
        )}

        {activeTab === 'search' && (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-white mb-2">Search Camere</h3>
              <p className="text-gray-400">În curând...</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Room Modal */}
      {showCreateRoom && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-xl rounded-3xl p-8 w-full max-w-md border border-white/20 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <h2 className="text-white text-2xl font-bold mb-2">{t('createNewRoom')}</h2>
              <p className="text-gray-300 text-sm">{t('startConversation')}</p>
            </div>
            
            <form onSubmit={createRoom} className="space-y-6">
              <div className="space-y-2">
                <label className="text-gray-300 text-sm font-medium">{t('roomName')}</label>
                <input
                  type="text"
                  placeholder={t('roomNamePlaceholder')}
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  className="w-full bg-white/10 backdrop-blur-sm text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white/20 placeholder-gray-400 border border-white/20 transition-all duration-200"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-gray-300 text-sm font-medium">{t('roomDescription')}</label>
                <textarea
                  placeholder={t('roomDescriptionPlaceholder')}
                  value={newRoomDescription}
                  onChange={(e) => setNewRoomDescription(e.target.value)}
                  className="w-full bg-white/10 backdrop-blur-sm text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white/20 placeholder-gray-400 border border-white/20 resize-none transition-all duration-200"
                  rows="3"
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-white font-medium">{t('privateRoom')}</div>
                    <div className="text-gray-400 text-xs">{t('privateRoomDesc')}</div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPrivate}
                    onChange={(e) => setIsPrivate(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateRoom(false)}
                  className="flex-1 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white py-3 rounded-xl transition-all duration-200 font-medium border border-white/20 hover:border-white/30"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-3 rounded-xl transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('createRoom')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Modal for Full View */}
      {selectedImageModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setSelectedImageModal(null)}>
          <div className="relative max-w-4xl max-h-full">
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}${selectedImageModal.url}`}
              alt="Full size"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setSelectedImageModal(null)}
              className="absolute top-4 right-4 bg-gray-900/70 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-900 transition-colors"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Share Modal - Facebook Style */}
      {shareModalPost && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={closeShareModal}>
          <div className="bg-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Partajează postarea</h3>
              <button
                onClick={closeShareModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <span className="text-xl">×</span>
              </button>
            </div>

            {/* Share Options */}
            <div className="p-4 space-y-4">
              {!shareOption ? (
                /* Option Selection */
                <div className="space-y-3">
                  <p className="text-gray-300 text-sm">Alege unde vrei să partajezi:</p>
                  
                  {/* Share to Room */}
                  <button
                    onClick={() => setShareOption('room')}
                    className="w-full p-4 bg-gray-700/50 hover:bg-gray-700/70 rounded-lg border border-white/10 transition-colors text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">🏠</span>
                      <div>
                        <div className="text-white font-medium">În camera ta</div>
                        <div className="text-gray-400 text-sm">Partajează în una din camerele tale create</div>
                      </div>
                    </div>
                  </button>

                  {/* Share to Friend */}
                  <button
                    onClick={() => setShareOption('friend')}
                    className="w-full p-4 bg-gray-700/50 hover:bg-gray-700/70 rounded-lg border border-white/10 transition-colors text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">👥</span>
                      <div>
                        <div className="text-white font-medium">Către un prieten</div>
                        <div className="text-gray-400 text-sm">Trimite direct unui utilizator favorit</div>
                      </div>
                    </div>
                  </button>
                </div>
              ) : (
                /* Share Form */
                <div className="space-y-4">
                  {/* Back Button */}
                  <button
                    onClick={() => setShareOption('')}
                    className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 text-sm"
                  >
                    <span>←</span>
                    <span>Înapoi</span>
                  </button>

                  {/* Target Selection */}
                  <div className="space-y-2">
                    <label className="text-white text-sm font-medium">
                      {shareOption === 'room' ? 'Alege camera:' : 'Alege prietenul:'}
                    </label>
                    
                    {shareOption === 'room' ? (
                      <select
                        value={selectedRoom}
                        onChange={(e) => setSelectedRoom(e.target.value)}
                        className="w-full p-3 bg-gray-700/50 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                      >
                        <option value="">Selectează camera...</option>
                        {userRooms.map(room => (
                          <option key={room.id} value={room.id}>{room.name}</option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={selectedFriend}
                        onChange={(e) => setSelectedFriend(e.target.value)}
                        className="w-full p-3 bg-gray-700/50 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                      >
                        <option value="">Selectează prietenul...</option>
                        {userFavorites.map(friend => (
                          <option key={friend.id} value={friend.id}>{friend.name} (@{friend.nickname})</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Comment Section */}
                  <div className="space-y-2">
                    <label className="text-white text-sm font-medium">
                      Adaugă un comentariu (opțional):
                    </label>
                    <textarea
                      value={shareComment}
                      onChange={(e) => setShareComment(e.target.value)}
                      placeholder="Scrie un comentariu despre această postare..."
                      className="w-full p-3 bg-gray-700/50 border border-white/20 rounded-lg text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                      rows="3"
                    />
                  </div>

                  {/* Original Post Preview */}
                  <div className="bg-gray-700/30 border border-white/10 rounded-lg p-3">
                    <div className="text-xs text-gray-400 mb-2">Postarea originală:</div>
                    <div className="flex items-start space-x-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center text-white text-xs font-bold">
                        {shareModalPost.user_nickname?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1">
                        <div className="text-white text-xs font-medium">{shareModalPost.user_name}</div>
                        <div className="text-gray-300 text-xs mt-1 line-clamp-3">
                          {shareModalPost.content.substring(0, 100)}
                          {shareModalPost.content.length > 100 && '...'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3 pt-2">
                    <button
                      onClick={closeShareModal}
                      className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    >
                      Anulează
                    </button>
                    <button
                      onClick={handleShare}
                      disabled={!((shareOption === 'room' && selectedRoom) || (shareOption === 'friend' && selectedFriend))}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                    >
                      Partajează
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Camera Modal */}
      {showCreateCamera && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowCreateCamera(false)}>
          <div className="bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h3 className="text-xl font-semibold text-white">Creează cameră nouă</h3>
              <button
                onClick={() => setShowCreateCamera(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <span className="text-xl">×</span>
              </button>
            </div>

            {/* Create Form */}
            <div className="p-6 space-y-4">
              {/* Camera Name */}
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">
                  Numele camerei *
                </label>
                <input
                  type="text"
                  value={newCamera.name}
                  onChange={(e) => setNewCamera(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="ex: Tech Talks România"
                  className="w-full p-3 bg-gray-700/50 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                  maxLength="50"
                />
              </div>

              {/* Theme */}
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">
                  Tema *
                </label>
                <input
                  type="text"
                  value={newCamera.theme}
                  onChange={(e) => setNewCamera(prev => ({ ...prev, theme: e.target.value }))}
                  placeholder="ex: Tehnologie, Politică, Gaming"
                  className="w-full p-3 bg-gray-700/50 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                  maxLength="30"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">
                  Descriere
                </label>
                <textarea
                  value={newCamera.description}
                  onChange={(e) => setNewCamera(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrie pe scurt despre ce este camera ta..."
                  className="w-full p-3 bg-gray-700/50 border border-white/20 rounded-lg text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                  rows="3"
                  maxLength="200"
                />
              </div>

              {/* Visibility */}
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">
                  Vizibilitate
                </label>
                <select
                  value={newCamera.visibility}
                  onChange={(e) => setNewCamera(prev => ({ ...prev, visibility: e.target.value }))}
                  className="w-full p-3 bg-gray-700/50 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                >
                  <option value="public">🌍 Publică - Oricine poate să se alăture</option>
                  <option value="private">🔒 Privată - Doar tu și persoanele invitate</option>
                  <option value="invite-only">📧 Doar cu invitație - Membri acceptați manual</option>
                </select>
              </div>

              {/* Rules */}
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">
                  Reguli (opțional)
                </label>
                <textarea
                  value={newCamera.rules}
                  onChange={(e) => setNewCamera(prev => ({ ...prev, rules: e.target.value }))}
                  placeholder="1. Respectă ceilalți membri&#10;2. Postează doar conținut relevant&#10;3. Fără spam sau reclame"
                  className="w-full p-3 bg-gray-700/50 border border-white/20 rounded-lg text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                  rows="4"
                />
              </div>

              {/* Max Members */}
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">
                  Numărul maxim de membri
                </label>
                <select
                  value={newCamera.maxMembers}
                  onChange={(e) => setNewCamera(prev => ({ ...prev, maxMembers: parseInt(e.target.value) }))}
                  className="w-full p-3 bg-gray-700/50 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                >
                  <option value="50">50 membri</option>
                  <option value="100">100 membri</option>
                  <option value="200">200 membri</option>
                  <option value="500">500 membri</option>
                  <option value="1000">1000 membri</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowCreateCamera(false)}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Anulează
                </button>
                <button
                  onClick={handleCreateCamera}
                  disabled={!newCamera.name.trim() || !newCamera.theme.trim()}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                >
                  Creează camera
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main App Component  
const App = () => {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
};

// Componenta FriendsBar a fost eliminată definitiv
// Nu mai afișează bulele cu litere din colțul din dreapta jos

// Main App Component cu State Management și Logic
const AppContent = () => {
  // === NEW X PLATFORM STATES - MOVED UP FOR PROPER SCOPE === 
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  
  // Following/Followers System (replacing favorites)
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  
  // Trending hashtags
  const [trendingHashtags, setTrendingHashtags] = useState([]);
  
  // Posts with X-style interactions
  const [postsInteractions, setPostsInteractions] = useState({});
  
  // Reply/Quote Tweet states
  const [replyToPost, setReplyToPost] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [quotePost, setQuotePost] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  
  // User Profile state
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  
  // Search state
  const [showSearchPage, setShowSearchPage] = useState(false);
  
  // Direct Messages state
  const [showDirectMessages, setShowDirectMessages] = useState(false);
  const [dmInitialUserId, setDmInitialUserId] = useState(null);
  
  // Timeline mode state (chronological or for-you)
  const [timelineMode, setTimelineMode] = useState('chronological'); // 'chronological' or 'for-you'
  
  // Moderation state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTarget, setReportTarget] = useState({ type: null, id: null, name: null });
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockTarget, setBlockTarget] = useState({ id: null, name: null });
  
  // Comments state
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [commentsForPost, setCommentsForPost] = useState(null);
  const [postComments, setPostComments] = useState([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  // === EXISTING STATES ===
  const [activeTab, setActiveTab] = useState('world-chat');
  const [isLogin, setIsLogin] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showPrivateChat, setShowPrivateChat] = useState(false);
  const [privateChatUser, setPrivateChatUser] = useState(null);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [newPrivateMessage, setNewPrivateMessage] = useState('');
  const [isPrivateChatUserFriend, setIsPrivateChatUserFriend] = useState(false);
  
  // Post creation states
  const [showPostModal, setShowPostModal] = useState(false);
  const [newPost, setNewPost] = useState('');
  const [linkPreview, setLinkPreview] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  
  // World Chat posts state - moved here so createPost can update it
  const [posts, setPosts] = useState([]);
  
  // World Chat typing indicator ref
  const worldChatTypingRef = useRef(null);
  
  // === AUTH AND CORE STATES ===
  const { user, loading, token } = useAuth();

  // === X PLATFORM API FUNCTIONS ===

  // Follow/Unfollow System
  const handleFollow = async (userId, shouldFollow) => {
    try {
      const endpoint = shouldFollow ? 'follow' : 'unfollow';
      const method = shouldFollow ? 'POST' : 'DELETE';
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${endpoint}/${userId}`, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Update following list
        if (shouldFollow) {
          loadFollowing();
        } else {
          setFollowing(prev => prev.filter(f => f.id !== userId));
        }
        
        // Update suggested users
        setSuggestedUsers(prev => 
          prev.map(u => u.id === userId ? { ...u, isFollowing: shouldFollow } : u)
        );
        
        // Create notification for the followed user
        if (shouldFollow) {
          await createNotification(userId, NOTIFICATION_TYPES.FOLLOW, null);
        }
      }
    } catch (error) {
      console.error('Follow/unfollow error:', error);
    }
  };

  // Open Direct Messages with specific user
  const openDMWithUser = (userId) => {
    setDmInitialUserId(userId);
    setShowDirectMessages(true);
  };

  // Load following list
  const loadFollowing = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/following`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFollowing(data);
      }
    } catch (error) {
      console.error('Error loading following:', error);
    }
  };

  // Load followers list  
  const loadFollowers = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/followers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFollowers(data);
      }
    } catch (error) {
      console.error('Error loading followers:', error);
    }
  };

  // Post Interactions
  const handleLike = async (postId, shouldLike) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/world-chat/posts/${postId}/like`, {
        method: shouldLike ? 'POST' : 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Update local interactions state
        setPostsInteractions(prev => ({
          ...prev,
          [postId]: {
            ...prev[postId],
            likes: data.like_count,
            isLiked: shouldLike
          }
        }));
      }
    } catch (error) {
      console.error('Like error:', error);
    }
  };

  const handleRepost = async (postId, shouldRepost) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/world-chat/posts/${postId}/repost`, {
        method: shouldRepost ? 'POST' : 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Update local interactions state
        setPostsInteractions(prev => ({
          ...prev,
          [postId]: {
            ...prev[postId],
            reposts: data.repost_count,
            isReposted: shouldRepost
          }
        }));
      }
    } catch (error) {
      console.error('Repost error:', error);
    }
  };

  // Handle Quote - opens the quote modal
  const handleQuote = (postId) => {
    const post = posts?.find(p => p.id === postId);
    if (post) {
      setQuotePost(post);
      setShowQuoteModal(true);
    }
  };

  // Submit Quote Post
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);
  
  const handleSubmitQuote = async (originalPostId, quoteText) => {
    if (!token) return;
    setIsSubmittingQuote(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/world-chat/posts/quote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          quoted_post_id: originalPostId,
          content: quoteText
        })
      });
      
      if (response.ok) {
        const newQuotePost = await response.json();
        // Add the new quote post to the top of the feed
        setPosts(prev => [newQuotePost, ...prev]);
        setShowQuoteModal(false);
        setQuotePost(null);
        
        // Update repost count on the original post
        setPostsInteractions(prev => ({
          ...prev,
          [originalPostId]: {
            ...prev[originalPostId],
            repost_count: (prev[originalPostId]?.repost_count || 0) + 1
          }
        }));
      }
    } catch (error) {
      console.error('Error creating quote post:', error);
    } finally {
      setIsSubmittingQuote(false);
    }
  };

  const handleReply = async (postId) => {
    // Open comments modal
    const post = posts?.find(p => p.id === postId);
    setCommentsForPost(post);
    setShowCommentsModal(true);
    await loadComments(postId);
  };

  // Navigate to user profile
  const handleViewProfile = (userId) => {
    setProfileUserId(userId);
    setShowUserProfile(true);
  };

  const loadComments = async (postId) => {
    if (!token) return;
    setIsLoadingComments(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/world-chat/posts/${postId}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPostComments(data);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleAddComment = async (postId, content) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/world-chat/posts/${postId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });
      
      if (response.ok) {
        const newComment = await response.json();
        setPostComments(prev => [newComment, ...prev]);
        
        // Update comments count in interactions
        setPostsInteractions(prev => ({
          ...prev,
          [postId]: {
            ...prev[postId],
            comments_count: (prev[postId]?.comments_count || 0) + 1
          }
        }));
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/world-chat/posts/${postId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setPostComments(prev => prev.filter(c => c.id !== commentId));
        
        // Update comments count in interactions
        setPostsInteractions(prev => ({
          ...prev,
          [postId]: {
            ...prev[postId],
            comments_count: Math.max((prev[postId]?.comments_count || 0) - 1, 0)
          }
        }));
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleShare = async (postId) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'VONEX Post',
          text: 'Check out this post on VONEX',
          url: `${window.location.origin}/post/${postId}`
        });
      } catch (error) {
        console.error('Share error:', error);
      }
    } else {
      // Fallback - copy to clipboard
      const url = `${window.location.origin}/post/${postId}`;
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  // Notifications System
  const loadNotifications = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Convert backend format to frontend format
        const formatted = data.map(n => ({
          id: n.id,
          type: n.type,
          actor: {
            id: n.actor_id,
            name: n.actor_name,
            nickname: n.actor_nickname,
            avatar: n.actor_avatar
          },
          post_id: n.post_id,
          post_content: n.post_content,
          created_at: n.created_at,
          read: n.is_read
        }));
        setNotifications(formatted);
        setUnreadNotificationsCount(formatted.filter(n => !n.read).length);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
      setUnreadNotificationsCount(0);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        );
        setUnreadNotificationsCount(prev => Math.max(prev - 1, 0));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadNotificationsCount(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // createNotification is no longer needed - backend creates notifications automatically

  // Poll notifications every 30 seconds for real-time updates
  useEffect(() => {
    if (!token) return;
    
    // Initial load
    loadNotifications();
    
    // Set up polling
    const pollInterval = setInterval(() => {
      loadNotifications();
    }, 30000); // 30 seconds
    
    return () => clearInterval(pollInterval);
  }, [token]);

  // Load trending hashtags
  const loadTrendingHashtags = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/world-chat/trending-hashtags`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Convert backend format to frontend format
        const formatted = data.map(h => ({
          tag: h.hashtag.replace('#', ''),
          posts: h.post_count
        }));
        setTrendingHashtags(formatted);
      }
    } catch (error) {
      console.error('Error loading trending hashtags:', error);
    }
  };

  // Load suggested users
  const loadSuggestedUsers = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/suggestions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Convert backend format to frontend format
        const formatted = data.map(u => ({
          id: u.id,
          name: `${u.first_name} ${u.last_name}`.trim() || u.nickname,
          nickname: u.nickname,
          avatar_url: u.avatar_url,
          isFollowing: u.is_following
        }));
        setSuggestedUsers(formatted);
      }
    } catch (error) {
      console.error('Error loading suggested users:', error);
    }
  };

  // === X PLATFORM USE EFFECTS ===
  
  // Load X platform data on mount and token change
  useEffect(() => {
    if (token) {
      loadNotifications();
      loadFollowing();
      loadFollowers();
      loadTrendingHashtags();
      loadSuggestedUsers();
    }
  }, [token]);

  // Load trending hashtags periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (token) {
        loadTrendingHashtags();
      }
    }, 5 * 60 * 1000); // Every 5 minutes
    
    return () => clearInterval(interval);
  }, [token]);

  // World Chat typing indicator - notify when user is typing a post
  useEffect(() => {
    if (newPost && newPost.trim().length > 0) {
      // User started typing
      worldChatTypingRef.current?.notifyTypingStart();
    } else {
      // User stopped typing or cleared text
      worldChatTypingRef.current?.notifyTypingStop();
    }
  }, [newPost]);

  // Link preview detection - detectează URL-uri în text și generează preview
  useEffect(() => {
    const detectAndPreviewLinks = async () => {
      // Safety checks
      if (!newPost || !newPost.trim() || !token) return;

      // Dacă există deja imagini atașate, nu genera link preview
      if (uploadedImages.length > 0) {
        setLinkPreview(null);
        setIsLoadingPreview(false);
        return;
      }

      try {
        // Regex pentru detectarea URL-urilor
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const urls = newPost.match(urlRegex);

        if (urls && urls.length > 0) {
          const url = urls[0]; // Folosim primul URL găsit
          
          // Dacă URL-ul este diferit de cel anterior, generează preview nou
          if (!linkPreview || linkPreview.url !== url) {
            setIsLoadingPreview(true);
            
            try {
              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/world-chat/link-preview`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ url: url })
              });

              if (response.ok) {
                const preview = await response.json();
                setLinkPreview(preview);
              } else {
                // Dacă nu se poate genera preview, șterge preview-ul existent
                setLinkPreview(null);
              }
            } catch (error) {
              console.error('Error generating link preview:', error);
              setLinkPreview(null);
            } finally {
              setIsLoadingPreview(false);
            }
          }
        } else {
          // Nu sunt URL-uri în text, șterge preview-ul
          setLinkPreview(null);
          setIsLoadingPreview(false);
        }
      } catch (error) {
        console.error('Error in link preview detection:', error);
        setLinkPreview(null);
        setIsLoadingPreview(false);
      }
    };

    // Debounce pentru a nu face prea multe cereri
    const timeoutId = setTimeout(detectAndPreviewLinks, 1000);
    return () => clearTimeout(timeoutId);
  }, [newPost, token, linkPreview, uploadedImages.length]); // Adăugat uploadedImages.length în dependencies

  // Functions for private chat
  const openPrivateChat = (chatUser) => {
    setPrivateChatUser(chatUser);
    setShowPrivateChat(true);
    fetchPrivateMessages(chatUser.id);
    checkIfUserIsFriend(chatUser.id);
  };

  // Check if user is friend
  const checkIfUserIsFriend = async (userId) => {
    try {
      const response = await api.get(`${API}/friends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const isFriend = response.data.some(friend => friend.friend_user_id === userId);
      setIsPrivateChatUserFriend(isFriend);
    } catch (error) {
      console.error('Failed to check friend status:', error);
      setIsPrivateChatUserFriend(false);
    }
  };

  // Toggle friend status
  const toggleFriendStatus = async () => {
    if (!privateChatUser) return;

    try {
      if (isPrivateChatUserFriend) {
        // Remove from friends - need to implement unfriend endpoint
        alert('Funcționalitatea de eliminare din favorite va fi implementată în curând.');
      } else {
        // Add to friends
        await api.post(`${API}/friends/request`, {
          friend_user_id: privateChatUser.id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsPrivateChatUserFriend(true);
        alert('Utilizator adăugat la favorite!');
      }
    } catch (error) {
      console.error('Failed to toggle friend status:', error);
      alert('Nu s-a putut modifica statusul de favorit.');
    }
  };

  const fetchPrivateMessages = async (userId) => {
    try {
      const response = await api.get(`${API}/private-messages/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrivateMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch private messages:', error);
    }
  };

  const sendPrivateMessage = async (e) => {
    e.preventDefault();
    if (!newPrivateMessage.trim() || !privateChatUser) return;

    try {
      const response = await api.post(`${API}/private-messages`, {
        content: newPrivateMessage.trim(),
        recipient_id: privateChatUser.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPrivateMessages(prev => [...prev, response.data]);
      setNewPrivateMessage('');
    } catch (error) {
      console.error('Failed to send private message:', error);
      alert('Nu s-a putut trimite mesajul privat. Încearcă din nou.');
    }
  };

  // Post creation functions
  const handleImageUpload = async (filesOrEvent) => {
    // Determinăm dacă primim un event object sau direct files array
    let files;
    if (filesOrEvent && filesOrEvent.target && filesOrEvent.target.files) {
      // Event object din input file
      files = Array.from(filesOrEvent.target.files);
    } else if (Array.isArray(filesOrEvent)) {
      // Array de files direct
      files = filesOrEvent;
    } else {
      console.error('Invalid files parameter:', filesOrEvent);
      alert('Format invalid de fișiere');
      return;
    }

    for (const file of files) {
      if (file.type.startsWith('image/')) {
        const formData = new FormData();
        formData.append('file', file);
        
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/world-chat/upload-image`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formData
          });
          
          if (response.ok) {
            const imageData = await response.json();
            setUploadedImages(prev => [...prev, imageData]);
          } else {
            const errorData = await response.json();
            console.error('Upload error:', errorData);
            alert('Eroare la încărcarea imaginii: ' + (errorData.detail || 'Unknown error'));
          }
        } catch (error) {
          console.error('Error uploading image:', error);
          alert('Eroare la încărcarea imaginii');
        }
      } else {
        alert('Tip de fișier nesuportat. Doar imagini sunt permise.');
      }
    }
  };

  const removeImage = (imageId) => {
    setUploadedImages(prev => prev.filter(img => img.id !== imageId));
  };

  const createPost = async () => {
    if (!newPost.trim() && uploadedImages.length === 0) {
      return;
    }

    try {
      console.log('🚀 Creating post with content:', newPost);
      
      // Extrage primul URL din text pentru link preview
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const urls = newPost.match(urlRegex);
      const linkUrl = urls ? urls[0] : null;

      console.log('📤 Sending POST request to backend...');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/world-chat/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          content: newPost,
          images: uploadedImages.map(img => img.id),
          link_url: linkUrl
        })
      });

      console.log('📋 Response status:', response.status, response.statusText);

      if (response.ok) {
        const newPostData = await response.json();
        console.log('✅ New post data received:', newPostData);
        
        // Add the new post to the beginning of the posts array (like X/Twitter)
        console.log('🔄 Updating posts state...');
        setPosts(prevPosts => {
          console.log('📊 Current posts count:', prevPosts.length);
          const updatedPosts = [newPostData, ...prevPosts];
          console.log('📊 Updated posts count:', updatedPosts.length);
          console.log('🆕 First post after update:', updatedPosts[0]);
          return updatedPosts;
        });
        
        // Clear form
        setNewPost('');
        setLinkPreview(null);
        setUploadedImages([]);
        setShowPostModal(false);
        
        // Stop typing indicator
        worldChatTypingRef.current?.notifyTypingStop();
        
        console.log('✅ Post created and added to feed successfully!');
        // Optional: Show success message briefly
        // alert('Postarea a fost creată cu succes!');
      } else {
        const errorData = await response.json();
        console.error('❌ Error creating post:', errorData);
        alert('Eroare la crearea postării: ' + (errorData.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error creating post:', error);
      alert('Eroare de conexiune la crearea postării');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <LoginForm
        isLogin={isLogin}
        onToggle={() => setIsLogin(!isLogin)}
      />
    );
  }

  if (showAccountSettings) {
    return (
      <AccountSettings onBack={() => setShowAccountSettings(false)} />
    );
  }

  if (selectedRoom) {
    return (
      <ChatRoom
        room={selectedRoom}
        onBack={() => setSelectedRoom(null)}
      />
    );
  }

  // User Profile View
  if (showUserProfile && profileUserId) {
    return (
      <UserProfile
        userId={profileUserId}
        token={token}
        currentUserId={user?.id}
        onBack={() => {
          setShowUserProfile(false);
          setProfileUserId(null);
        }}
        onLike={handleLike}
        onRepost={handleRepost}
        onQuote={handleQuote}
        onComment={handleReply}
        onShare={handleShare}
      />
    );
  }

  // Direct Messages View
  if (showDirectMessages) {
    return (
      <DirectMessages
        token={token}
        currentUserId={user?.id}
        onBack={() => {
          setShowDirectMessages(false);
          setDmInitialUserId(null);
        }}
        onViewProfile={(userId) => {
          setShowDirectMessages(false);
          handleViewProfile(userId);
        }}
        initialConversationUserId={dmInitialUserId}
      />
    );
  }

  // Search Page View
  if (showSearchPage) {
    return (
      <SearchPage
        token={token}
        currentUserId={user?.id}
        onBack={() => setShowSearchPage(false)}
        onViewProfile={(userId) => {
          setShowSearchPage(false);
          handleViewProfile(userId);
        }}
        onLike={handleLike}
        onRepost={handleRepost}
        onQuote={handleQuote}
        onComment={handleReply}
        onShare={handleShare}
      />
    );
  }

  return (
    <>
      <RoomList 
        onRoomSelect={setSelectedRoom}
        onAccountSettings={() => setShowAccountSettings(true)}
        showPostModal={showPostModal}
        setShowPostModal={setShowPostModal}
        newPost={newPost}
        setNewPost={setNewPost}
        handleImageUpload={handleImageUpload}
        createPost={createPost}
        uploadedImages={uploadedImages}
        removeImage={removeImage}
        linkPreview={linkPreview}
        isLoadingPreview={isLoadingPreview}
        notifications={notifications}
        unreadNotificationsCount={unreadNotificationsCount}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        markNotificationRead={markNotificationRead}
        markAllNotificationsRead={markAllNotificationsRead}
        following={following}
        followers={followers}
        suggestedUsers={suggestedUsers}
        trendingHashtags={trendingHashtags}
        postsInteractions={postsInteractions}
        onLike={handleLike}
        onRepost={handleRepost}
        onQuote={handleQuote}
        onReply={handleReply}
        onShare={handleShare}
        onFollow={handleFollow}
        onViewProfile={handleViewProfile}
        setShowSearchPage={setShowSearchPage}
        setShowDirectMessages={setShowDirectMessages}
        timelineMode={timelineMode}
        setTimelineMode={setTimelineMode}
        posts={posts}
        setPosts={setPosts}
        worldChatTypingRef={worldChatTypingRef}
      />
      {/* FriendsBar eliminată definitiv - nu mai apar bulele cu litere din dreapta jos */}
      
      {/* Global Private Chat Overlay - Responsive */}
      {showPrivateChat && privateChatUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-gray-800/95 backdrop-blur-xl rounded-xl border border-white/20 shadow-2xl w-full max-w-full sm:max-w-2xl h-full sm:h-96 flex flex-col">
            {/* Private Chat Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full overflow-hidden border border-purple-500/30">
                  {privateChatUser.avatar_url ? (
                    privateChatUser.avatar_url.startsWith('data:') ? (
                      <img 
                        src={privateChatUser.avatar_url} 
                        alt="Avatar" 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <div 
                        className="w-full h-full"
                        dangerouslySetInnerHTML={{ 
                          __html: defaultAvatars.find(a => a.id === privateChatUser.avatar_url)?.svg || '' 
                        }}
                      />
                    )
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">
                        {privateChatUser.nickname?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold">Chat privat cu {privateChatUser.nickname}</h3>
                  <p className="text-gray-400 text-sm flex items-center">
                    Conversație privată
                    {isPrivateChatUserFriend && (
                      <span className="ml-2 text-green-400 text-xs">★ Favorit</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleFriendStatus}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${
                    isPrivateChatUserFriend 
                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                      : 'bg-purple-600 hover:bg-purple-700 text-white'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isPrivateChatUserFriend ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    )}
                  </svg>
                  <span>{isPrivateChatUserFriend ? 'FAVORIT' : 'FAVORIT'}</span>
                </button>
                <button
                  onClick={() => {
                    setShowPrivateChat(false);
                    setPrivateChatUser(null);
                    setPrivateMessages([]);
                    setIsPrivateChatUserFriend(false);
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Private Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {privateMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                    msg.sender_id === user?.id
                      ? 'bg-gradient-to-br from-purple-600 to-blue-600 text-white'
                      : 'bg-white/10 text-gray-100'
                  }`}>
                    <div className="break-words">{msg.content}</div>
                    <div className={`text-xs mt-1 opacity-70 ${
                      msg.sender_id === user?.id ? 'text-purple-100' : 'text-gray-400'
                    }`}>
                      {formatTime(msg.created_at)}
                    </div>
                  </div>
                </div>
              ))}
              {privateMessages.length === 0 && (
                <div className="text-center text-gray-400">
                  <p>Încă nu aveți mesaje. Începeți conversația!</p>
                </div>
              )}
            </div>

            {/* Messenger-style Input pentru Private Chat */}
            <div className="p-4 border-t border-white/10">
              <MessengerInput
                value={newPrivateMessage}
                onChange={setNewPrivateMessage}
                onSubmit={() => {
                  if (newPrivateMessage.trim()) {
                    const mockEvent = { preventDefault: () => {} };
                    sendPrivateMessage(mockEvent);
                  }
                }}
                placeholder={`Mesaj pentru ${privateChatUser.nickname}...`}
                maxLength={1000}
              />
            </div>
          </div>
        </div>
      )}

      {/* Post Creation Modal */}
      <PostCreationModal
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
        newPost={newPost}
        setNewPost={setNewPost}
        handleImageUpload={handleImageUpload}
        createPost={createPost}
        uploadedImages={uploadedImages}
        removeImage={removeImage}
        linkPreview={linkPreview}
        isLoadingPreview={isLoadingPreview}
      />

        {/* Notifications Panel */}
        <NotificationsPanel
          notifications={notifications}
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          onMarkRead={markNotificationRead}
          onMarkAllRead={markAllNotificationsRead}
          onUserClick={(userId) => {
            setShowNotifications(false);
            handleViewProfile(userId);
          }}
        />
        
        {/* Quote Modal */}
        <QuoteModal
          isOpen={showQuoteModal}
          onClose={() => {
            setShowQuoteModal(false);
            setQuotePost(null);
          }}
          originalPost={quotePost}
          onSubmitQuote={handleSubmitQuote}
          isSubmitting={isSubmittingQuote}
        />
        
        {/* Comments Modal */}
        <CommentsModal
          isOpen={showCommentsModal}
          onClose={() => {
            setShowCommentsModal(false);
            setCommentsForPost(null);
            setPostComments([]);
          }}
          postId={commentsForPost?.id}
          postContent={commentsForPost?.content}
          comments={postComments}
          onAddComment={handleAddComment}
          onDeleteComment={handleDeleteComment}
          currentUserId={user?.id}
          isLoading={isLoadingComments}
        />
    </>
  );
};

export default App;